import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Plus, RefreshCw, X, Check, Trash2, Edit3, Settings, TableProperties, FileSpreadsheet } from 'lucide-react';
import { strToU8, zipSync } from 'fflate';
import * as XLSX from 'xlsx';
import axios from 'axios';

const API_BASE_URL = 'https://baustellenmgmt.mas123.de/api';

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getCurrentWeek() {
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - today.getDay() + 1);
  return formatDate(monday);
}

function getWeekDates(startDate, weeksToShow) {
  const dates = [];
  const start = new Date(startDate);
  const daysToShow = Math.max(1, weeksToShow) * 7;
  for (let i = 0; i < daysToShow; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    dates.push({
      date: formatDate(date),
      dayName: date.toLocaleDateString('de-DE', { weekday: 'long' }),
      dayNumber: date.getDate(),
      month: date.toLocaleDateString('de-DE', { month: 'short' }),
      isToday: formatDate(date) === formatDate(new Date())
    });
  }
  return dates;
}

function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatMasterDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('de-DE');
}

function buildOdsContent(rows, sheetName) {
  const xmlRows = rows.map((row) => (
    `<table:table-row>${row.map((cell) => (
      `<table:table-cell office:value-type="string"><text:p>${escapeHtml(cell ?? '')}</text:p></table:table-cell>`
    )).join('')}</table:table-row>`
  )).join('');

  return `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<office:document-content xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" ` +
    `xmlns:table="urn:oasis:names:tc:opendocument:xmlns:table:1.0" ` +
    `xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0" office:version="1.2">\n` +
    ` <office:body>\n` +
    `  <office:spreadsheet>\n` +
    `   <table:table table:name="${escapeHtml(sheetName)}">\n` +
    `    ${xmlRows}\n` +
    `   </table:table>\n` +
    `  </office:spreadsheet>\n` +
    ` </office:body>\n` +
    `</office:document-content>`;
}

function downloadOds(filename, rows, sheetName) {
  const contentXml = buildOdsContent(rows, sheetName);
  const manifestXml = `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<manifest:manifest xmlns:manifest="urn:oasis:names:tc:opendocument:xmlns:manifest:1.0" manifest:version="1.2">\n` +
    ` <manifest:file-entry manifest:media-type="application/vnd.oasis.opendocument.spreadsheet" manifest:full-path="/" />\n` +
    ` <manifest:file-entry manifest:media-type="text/xml" manifest:full-path="content.xml" />\n` +
    `</manifest:manifest>`;

  const zipped = zipSync({
    'mimetype': [strToU8('application/vnd.oasis.opendocument.spreadsheet'), { level: 0 }],
    'content.xml': strToU8(contentXml),
    'META-INF/manifest.xml': strToU8(manifestXml),
  });

  const blob = new Blob([zipped], { type: 'application/vnd.oasis.opendocument.spreadsheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getContrastTextColor(hex) {
  if (!hex) return '#111827';
  const safe = hex.startsWith('#') ? hex.slice(1) : hex;
  if (safe.length !== 6) return '#111827';
  const num = parseInt(safe, 16);
  if (Number.isNaN(num)) return '#111827';
  const r = num >> 16;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
  return luminance > 160 ? '#111827' : '#ffffff';
}

const STATUS_OPTIONS = [
  { value: 'GEPLANT', label: 'Geplant' },
  { value: 'AKTIV', label: 'Aktiv' },
  { value: 'PAUSIERT', label: 'Pausiert' },
  { value: 'ABGESCHLOSSEN', label: 'Abgeschlossen' }
];

const DEFAULT_BAUSTELLE = {
  name: '',
  kunde_name: '',
  status: 'GEPLANT',
  start_datum: '',
  ende_datum: '',
  beschreibung: '',
  farbe: '#3B82F6',
  nextcloud_ordner_pfad: ''
};

function formatAmountDe(value) {
  if (value === null || value === undefined || value === '') return '-';
  const num = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function compareText(a, b) {
  return String(a || '').localeCompare(String(b || ''), 'de', { sensitivity: 'base' });
}

function getBaustelleSortValue(baustelle, field) {
  switch (field) {
    case 'kunde_name':
      return baustelle.kunde_name || baustelle.name || '';
    case 'nummer':
      return baustelle.nummer || '';
    case 'start_datum':
      return baustelle.start_datum || '';
    case 'status':
      return baustelle.status || '';
    case 'farbe':
      return baustelle.farbe || '';
    default:
      return baustelle.kunde_name || '';
  }
}

function BaustellenView({ token }) {
  const [baustellen, setBaustellen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('AKTIV');
  const [sortConfig, setSortConfig] = useState({ field: 'kunde_name', direction: 'asc' });
  const [hoveredBaustelleId, setHoveredBaustelleId] = useState(null);
  const [editingBaustelle, setEditingBaustelle] = useState(null);
  const [newBaustelle, setNewBaustelle] = useState(null);
  const [statsBaustelle, setStatsBaustelle] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  const [showMaterialDetails, setShowMaterialDetails] = useState(false);
  const [showMasterList, setShowMasterList] = useState(false);
  const [showManualHoursModal, setShowManualHoursModal] = useState(false);
  const [manualHoursSaving, setManualHoursSaving] = useState(false);
  const [manualHoursForm, setManualHoursForm] = useState({
    id: null,
    datum: '',
    stunden: '',
    beschreibung: ''
  });
  const [statsWeekStart, setStatsWeekStart] = useState(getCurrentWeek());
  const [statsWeeksToShow, setStatsWeeksToShow] = useState(() => {
    const stored = localStorage.getItem('baustellenStatsDefaultWeeks');
    const parsed = stored ? parseInt(stored, 10) : 2;
    return Number.isNaN(parsed) ? 2 : Math.max(1, parsed);
  });
  const [statsShowPerson, setStatsShowPerson] = useState(() => {
    const stored = localStorage.getItem('baustellenStatsShowPerson');
    return stored ? stored === 'true' : false;
  });
  const [showNextcloudPicker, setShowNextcloudPicker] = useState(false);
  const [nextcloudLoading, setNextcloudLoading] = useState(false);
  const [nextcloudError, setNextcloudError] = useState('');
  const [nextcloudCurrentPath, setNextcloudCurrentPath] = useState('');
  const [nextcloudDirs, setNextcloudDirs] = useState([]);
  const [stundenverrechnungOverrides, setStundenverrechnungOverrides] = useState([]);
  const [editingStundenverrechnungOverride, setEditingStundenverrechnungOverride] = useState(null);
  const [newStundenverrechnungOverride, setNewStundenverrechnungOverride] = useState(null);
  const [rechnungsaufschlagOverrides, setRechnungsaufschlagOverrides] = useState([]);
  const [editingRechnungsaufschlagOverride, setEditingRechnungsaufschlagOverride] = useState(null);
  const [newRechnungsaufschlagOverride, setNewRechnungsaufschlagOverride] = useState(null);
  const [invoiceAssignmentsLoading, setInvoiceAssignmentsLoading] = useState(false);
  const [invoiceAssignments, setInvoiceAssignments] = useState([]);
  const [materialAssignmentsLoading, setMaterialAssignmentsLoading] = useState(false);
  const [materialAssignments, setMaterialAssignments] = useState([]);
  const [stundenverrechnungGlobal, setStundenverrechnungGlobal] = useState([]);
  const [rechnungsaufschlagGlobal, setRechnungsaufschlagGlobal] = useState([]);
  const [masterFilters, setMasterFilters] = useState({
    stunden: true,
    rechnungen: true,
    material: true,
    startDate: '',
    endDate: '',
  });
  const [masterInvoiceSurchargeInput, setMasterInvoiceSurchargeInput] = useState('0');
  const [masterInvoiceSurcharge, setMasterInvoiceSurcharge] = useState(0);
  const [invoiceDeleteDialog, setInvoiceDeleteDialog] = useState({
    open: false,
    invoice: null,
    fileAction: 'move_to_inbox',
    submitting: false,
  });
  const [showManualInvoiceModal, setShowManualInvoiceModal] = useState(false);
  const [manualInvoiceSaving, setManualInvoiceSaving] = useState(false);
  const [manualInvoiceForm, setManualInvoiceForm] = useState({
    datei_name: '',
    issuer: '',
    invoice_number: '',
    date: formatDate(new Date()),
    net_amount: '',
    tax: '',
    amount: '',
    bemerkung: '',
  });
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [importWizardBaustelle, setImportWizardBaustelle] = useState(null);
  const [importWizardStep, setImportWizardStep] = useState(1);
  const [importWizardLoading, setImportWizardLoading] = useState(false);
  const [importWizardError, setImportWizardError] = useState('');
  const [importWizardPath, setImportWizardPath] = useState('');
  const [importWizardDirs, setImportWizardDirs] = useState([]);
  const [importWizardFolderExists, setImportWizardFolderExists] = useState(false);
  const [importWizardFiles, setImportWizardFiles] = useState([]);
  const [selectedImportFile, setSelectedImportFile] = useState('');
  const [importPreviewRows, setImportPreviewRows] = useState([]);
  const [selectedImportRows, setSelectedImportRows] = useState([]);
  const [positionEditing, setPositionEditing] = useState(null);
  const [positionSaving, setPositionSaving] = useState(false);
  const [invoiceFileLoadingId, setInvoiceFileLoadingId] = useState(null);
  const formContainerRef = useRef(null);

  const loadBaustellen = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/baustellen/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBaustellen(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Baustellen:', error);
      alert('Fehler beim Laden der Baustellen');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    loadBaustellen();
  }, [loadBaustellen]);

  useEffect(() => {
    if (newBaustelle || editingBaustelle) {
      formContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [newBaustelle, editingBaustelle]);

  function normalizeBaustelle(data) {
    return {
      id: data.id,
      nummer: data.nummer || '',
      name: data.name || '',
      kunde_name: data.kunde_name || '',
      status: data.status || 'GEPLANT',
      start_datum: data.start_datum || '',
      ende_datum: data.ende_datum || '',
      beschreibung: data.beschreibung || '',
      farbe: data.farbe || '#3B82F6',
      nextcloud_ordner_pfad: data.nextcloud_ordner_pfad || ''
    };
  }

  async function startEditBaustelle(baustelle, options = {}) {
    try {
      const [response, overrideResponse, invoiceOverrideResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/baustellen/baustellen/${baustelle.id}/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/baustellen/stundenverrechnung-baustelle/`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { baustelle: baustelle.id }
        }),
        axios.get(`${API_BASE_URL}/baustellen/rechnungsaufschlag-baustelle/`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { baustelle: baustelle.id }
        }),
      ]);
      setEditingBaustelle(normalizeBaustelle(response.data));
      setStundenverrechnungOverrides(overrideResponse.data.results || overrideResponse.data || []);
      setRechnungsaufschlagOverrides(invoiceOverrideResponse.data.results || invoiceOverrideResponse.data || []);
      if (options.openFromDetails) {
        setStatsBaustelle(null);
        setStatsData(null);
        setShowDetails(false);
        setShowInvoiceDetails(false);
        setShowMaterialDetails(false);
        setShowMasterList(false);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Baustelle:', error);
      setEditingBaustelle(normalizeBaustelle(baustelle));
      setStundenverrechnungOverrides([]);
      setRechnungsaufschlagOverrides([]);
      if (options.openFromDetails) {
        setStatsBaustelle(null);
        setStatsData(null);
        setShowDetails(false);
        setShowInvoiceDetails(false);
        setShowMaterialDetails(false);
        setShowMasterList(false);
      }
    }
  }

  async function loadStundenverrechnungOverrides(baustelleId) {
    if (!baustelleId) {
      setStundenverrechnungOverrides([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/stundenverrechnung-baustelle/`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { baustelle: baustelleId }
      });
      setStundenverrechnungOverrides(response.data.results || response.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der baustellenspezifischen Stundenverrechnung:', error);
      setStundenverrechnungOverrides([]);
    }
  }

  async function loadRechnungsaufschlagOverrides(baustelleId) {
    if (!baustelleId) {
      setRechnungsaufschlagOverrides([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/rechnungsaufschlag-baustelle/`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { baustelle: baustelleId }
      });
      setRechnungsaufschlagOverrides(response.data.results || response.data || []);
    } catch (error) {
      console.error('Fehler beim Laden des baustellenspezifischen Rechnungsaufschlags:', error);
      setRechnungsaufschlagOverrides([]);
    }
  }

  async function saveBaustelle(baustelle) {
    const payload = {
      nummer: baustelle.nummer || '',
      name: baustelle.name,
      kunde_name: baustelle.kunde_name,
      status: baustelle.status,
      start_datum: baustelle.start_datum || null,
      ende_datum: baustelle.ende_datum || null,
      beschreibung: baustelle.beschreibung || '',
      farbe: baustelle.farbe || '#3B82F6',
      nextcloud_ordner_pfad: baustelle.nextcloud_ordner_pfad || ''
    };
    if (!baustelle.id && !baustelle.nummer) {
      delete payload.nummer;
    }

    try {
      if (baustelle.id) {
        await axios.put(
          `${API_BASE_URL}/baustellen/baustellen/${baustelle.id}/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/baustellen/baustellen/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      setEditingBaustelle(null);
      setNewBaustelle(null);
      setEditingStundenverrechnungOverride(null);
      setNewStundenverrechnungOverride(null);
      setStundenverrechnungOverrides([]);
      setEditingRechnungsaufschlagOverride(null);
      setNewRechnungsaufschlagOverride(null);
      setRechnungsaufschlagOverrides([]);
      loadBaustellen();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Speichern';
      alert(errorMsg);
    }
  }

  async function loadBaustelleStats(baustelle, options = {}) {
    setStatsBaustelle(baustelle);
    setStatsLoading(true);
    setStatsData(null);
    setShowInvoiceDetails(false);
    setShowMaterialDetails(false);
    setShowMasterList(false);
    if (!options.preserveView) {
      setShowDetails(false);
      setStatsWeekStart(getCurrentWeek());
      const storedWeeks = localStorage.getItem('baustellenStatsDefaultWeeks');
      const parsedWeeks = storedWeeks ? parseInt(storedWeeks, 10) : 2;
      setStatsWeeksToShow(Number.isNaN(parsedWeeks) ? 2 : Math.max(1, parsedWeeks));
    }

    try {
      const [reportResponse, manualResponse, invoiceResponse, materialResponse, globalRateResponse, overrideRateResponse, globalInvoiceSurchargeResponse, overrideInvoiceSurchargeResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/stunden/wochenberichte/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/baustellen/manuelle-stunden/`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { baustelle: baustelle.id }
        }),
        axios.get(`${API_BASE_URL}/baustellen/rechnungen/`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { baustelle: baustelle.id, page_size: 500 }
        }),
        axios.get(`${API_BASE_URL}/baustellen/kostenpositionen/`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { baustelle: baustelle.id, page_size: 500 }
        }),
        axios.get(`${API_BASE_URL}/baustellen/stundenverrechnung-global/`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { page_size: 500 }
        }),
        axios.get(`${API_BASE_URL}/baustellen/stundenverrechnung-baustelle/`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { baustelle: baustelle.id, page_size: 500 }
        }),
        axios.get(`${API_BASE_URL}/baustellen/rechnungsaufschlag-global/`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { page_size: 500 }
        }),
        axios.get(`${API_BASE_URL}/baustellen/rechnungsaufschlag-baustelle/`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { baustelle: baustelle.id, page_size: 500 }
        }),
      ]);
      const reports = reportResponse.data.results || reportResponse.data;
      const manualEntriesResponse = manualResponse.data.results || manualResponse.data;
      const invoiceResults = invoiceResponse.data.results || invoiceResponse.data || [];
      const materialResults = materialResponse.data.results || materialResponse.data || [];
      const globalRateResults = globalRateResponse.data.results || globalRateResponse.data || [];
      const overrideRateResults = overrideRateResponse.data.results || overrideRateResponse.data || [];
      const globalInvoiceSurchargeResults = globalInvoiceSurchargeResponse.data.results || globalInvoiceSurchargeResponse.data || [];
      const overrideInvoiceSurchargeResults = overrideInvoiceSurchargeResponse.data.results || overrideInvoiceSurchargeResponse.data || [];

      const entries = [];
      reports.forEach(report => {
        const mitarbeiterName = report.mitarbeiter_details
          ? `${report.mitarbeiter_details.first_name} ${report.mitarbeiter_details.last_name}`.trim()
          : 'Unbekannt';
        (report.stundeneintraege || []).forEach(entry => {
          if (entry.baustelle === baustelle.id) {
            entries.push({
              uid: `report-${entry.id}`,
              id: entry.id,
              datum: entry.datum,
              stunden: parseFloat(entry.stunden || 0),
              beschreibung: entry.beschreibung || '',
              mitarbeiterName,
              source: 'report'
            });
          }
        });
      });

      manualEntriesResponse.forEach(entry => {
        entries.push({
          uid: `manual-${entry.id}`,
          id: entry.id,
          datum: entry.datum,
          stunden: parseFloat(entry.stunden || 0),
          beschreibung: entry.beschreibung || '',
          mitarbeiterName: 'Manuell',
          source: 'manual'
        });
      });

      const perMitarbeiter = entries.reduce((acc, entry) => {
        acc[entry.mitarbeiterName] = (acc[entry.mitarbeiterName] || 0) + entry.stunden;
        return acc;
      }, {});

      const totalHours = entries.reduce((sum, entry) => sum + entry.stunden, 0);

      setStatsData({
        entries: entries.sort((a, b) => {
          const dateCompare = String(a.datum).localeCompare(String(b.datum));
          if (dateCompare !== 0) return dateCompare;
          return String(a.uid).localeCompare(String(b.uid));
        }),
        perMitarbeiter,
        totalHours
      });
      setInvoiceAssignments(Array.isArray(invoiceResults) ? invoiceResults : []);
      setMaterialAssignments(Array.isArray(materialResults) ? materialResults : []);
      setStundenverrechnungGlobal(Array.isArray(globalRateResults) ? globalRateResults : []);
      setStundenverrechnungOverrides(Array.isArray(overrideRateResults) ? overrideRateResults : []);
      setRechnungsaufschlagGlobal(Array.isArray(globalInvoiceSurchargeResults) ? globalInvoiceSurchargeResults : []);
      setRechnungsaufschlagOverrides(Array.isArray(overrideInvoiceSurchargeResults) ? overrideInvoiceSurchargeResults : []);
    } catch (error) {
      console.error('Fehler beim Laden der Statistiken:', error);
      alert('Fehler beim Laden der Statistiken');
      setInvoiceAssignments([]);
      setMaterialAssignments([]);
      setStundenverrechnungGlobal([]);
      setStundenverrechnungOverrides([]);
      setRechnungsaufschlagGlobal([]);
      setRechnungsaufschlagOverrides([]);
    }
    setStatsLoading(false);
  }

  const invoiceTotals = useMemo(() => {
    const toNumber = (value) => {
      if (value === null || value === undefined || value === '') return 0;
      const n = Number(value);
      return Number.isNaN(n) ? 0 : n;
    };
    let invoiceAmount = 0;
    let positionsAmount = 0;
    let positionsCount = 0;
    invoiceAssignments.forEach((inv) => {
      invoiceAmount += toNumber(inv.amount);
      (inv.positionen || []).forEach((pos) => {
        positionsCount += 1;
        positionsAmount += toNumber(pos.line_total);
      });
    });
    return {
      invoiceCount: invoiceAssignments.length,
      positionsCount,
      invoiceAmount,
      positionsAmount,
    };
  }, [invoiceAssignments]);

  const materialTotals = useMemo(() => {
    const toNumber = (value) => {
      if (value === null || value === undefined || value === '') return 0;
      const n = Number(value);
      return Number.isNaN(n) ? 0 : n;
    };
    const byCategory = {};
    let sum = 0;
    materialAssignments.forEach((entry) => {
      sum += toNumber(entry.gesamtpreis);
      const key = entry.kategorie || 'SONSTIG';
      byCategory[key] = (byCategory[key] || 0) + toNumber(entry.gesamtpreis);
    });
    return {
      count: materialAssignments.length,
      amount: sum,
      byCategory,
    };
  }, [materialAssignments]);

  const resolveStundenverrechnungForDate = useCallback((dateStr) => {
    const pickLatest = (list) => [...(list || [])]
      .filter((entry) => entry?.gueltig_ab && entry.gueltig_ab <= dateStr)
      .sort((a, b) => String(b.gueltig_ab).localeCompare(String(a.gueltig_ab)) || Number(b.id || 0) - Number(a.id || 0))[0];

    const override = pickLatest(stundenverrechnungOverrides);
    if (override) return Number(override.wert || 0);
    const global = pickLatest(stundenverrechnungGlobal);
    return global ? Number(global.wert || 0) : 0;
  }, [stundenverrechnungOverrides, stundenverrechnungGlobal]);

  const resolveRechnungsaufschlagForDate = useCallback((dateStr) => {
    const pickLatest = (list) => [...(list || [])]
      .filter((entry) => entry?.gueltig_ab && entry.gueltig_ab <= dateStr)
      .sort((a, b) => String(b.gueltig_ab).localeCompare(String(a.gueltig_ab)) || Number(b.id || 0) - Number(a.id || 0))[0];

    const override = pickLatest(rechnungsaufschlagOverrides);
    if (override) return Number(override.wert || 0);
    const global = pickLatest(rechnungsaufschlagGlobal);
    return global ? Number(global.wert || 0) : 0;
  }, [rechnungsaufschlagOverrides, rechnungsaufschlagGlobal]);

  useEffect(() => {
    if (!statsBaustelle?.id) return;
    const today = formatDate(new Date());
    const defaultSurcharge = resolveRechnungsaufschlagForDate(today);
    setMasterInvoiceSurcharge(defaultSurcharge);
    setMasterInvoiceSurchargeInput(String(defaultSurcharge).replace('.', ','));
  }, [statsBaustelle?.id, rechnungsaufschlagGlobal, rechnungsaufschlagOverrides, resolveRechnungsaufschlagForDate]);

  const masterListData = useMemo(() => {
    const includeHours = masterFilters.stunden;
    const includeInvoices = masterFilters.rechnungen;
    const includeMaterial = masterFilters.material;
    const startDate = masterFilters.startDate || '';
    const endDate = masterFilters.endDate || '';
    const grouped = new Map();
    const applyInvoiceSurcharge = (amount) => {
      const numericAmount = Number(amount || 0);
      if (Number.isNaN(numericAmount)) return 0;
      return numericAmount * (1 + (masterInvoiceSurcharge / 100));
    };

    const ensureDay = (dateKey) => {
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, {
          date: dateKey,
          hoursEntries: [],
          invoiceEntries: [],
          materialEntries: [],
          hoursCost: 0,
          invoiceCost: 0,
          materialCost: 0,
        });
      }
      return grouped.get(dateKey);
    };

    const dateInRange = (dateKey) => {
      if (!dateKey) return false;
      if (startDate && dateKey < startDate) return false;
      if (endDate && dateKey > endDate) return false;
      return true;
    };

    if (includeHours) {
      (statsData?.entries || []).forEach((entry) => {
        if (!dateInRange(entry.datum)) return;
        const day = ensureDay(entry.datum);
        const stunden = Number(entry.stunden || 0);
        const rate = resolveStundenverrechnungForDate(entry.datum);
        const cost = stunden * rate;
        day.hoursEntries.push({
          ...entry,
          stunden,
          rate,
          cost,
        });
        day.hoursCost += cost;
      });
    }

    if (includeInvoices) {
      (invoiceAssignments || []).forEach((entry) => {
        if (!dateInRange(entry.date)) return;
        const day = ensureDay(entry.date);
        const amount = applyInvoiceSurcharge(entry.amount || 0);
        day.invoiceEntries.push({
          ...entry,
          originalAmount: Number(entry.amount || 0),
          amount,
        });
        day.invoiceCost += amount;
      });
    }

    if (includeMaterial) {
      (materialAssignments || []).forEach((entry) => {
        if (!dateInRange(entry.datum)) return;
        const day = ensureDay(entry.datum);
        const amount = Number(entry.gesamtpreis || 0);
        day.materialEntries.push({ ...entry, amount });
        day.materialCost += amount;
      });
    }

    const dayRows = Array.from(grouped.values())
      .map((day) => {
        const total = day.hoursCost + day.invoiceCost + day.materialCost;
        const dateObj = new Date(day.date);
        return {
          ...day,
          total,
          year: dateObj.getFullYear(),
          month: dateObj.getMonth() + 1,
          monthLabel: dateObj.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
        };
      })
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));

    const rows = [];
    let cumulative = 0;
    let currentMonthKey = null;
    let currentMonthItems = [];

    const flushMonth = () => {
      if (currentMonthItems.length === 0) return;
      const monthSum = currentMonthItems.reduce((sum, item) => sum + item.total, 0);
      cumulative += monthSum;
      rows.push(...currentMonthItems);
      rows.push({
        type: 'summary',
        key: `${currentMonthKey}-summary`,
        label: `Summe Monat ${currentMonthItems[0].monthLabel}`,
        monthSum,
        cumulative,
      });
      currentMonthItems = [];
    };

    dayRows.forEach((day) => {
      const monthKey = `${day.year}-${String(day.month).padStart(2, '0')}`;
      if (currentMonthKey && currentMonthKey !== monthKey) {
        flushMonth();
      }
      currentMonthKey = monthKey;
      currentMonthItems.push({ ...day, type: 'day', key: day.date });
    });
    flushMonth();

    return rows;
  }, [statsData, invoiceAssignments, materialAssignments, masterFilters, masterInvoiceSurcharge, resolveStundenverrechnungForDate]);

  const filteredBaustellen = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = baustellen.filter((b) => {
      const matchSearch =
        !needle ||
        (b.name || '').toLowerCase().includes(needle) ||
        (b.nummer || '').toLowerCase().includes(needle) ||
        (b.kunde_name || '').toLowerCase().includes(needle);
      const matchStatus = !statusFilter || b.status === statusFilter;
      return matchSearch && matchStatus;
    });

    return filtered.sort((a, b) => {
      const left = getBaustelleSortValue(a, sortConfig.field);
      const right = getBaustelleSortValue(b, sortConfig.field);
      const baseResult = compareText(left, right) || compareText(a.name, b.name) || compareText(a.nummer, b.nummer);
      return sortConfig.direction === 'asc' ? baseResult : -baseResult;
    });
  }, [baustellen, search, statusFilter, sortConfig]);

  const statsWeekDates = getWeekDates(statsWeekStart, statsWeeksToShow);
  const statsMonthSegments = statsWeekDates.reduce((segments, day) => {
    const dateObj = new Date(day.date);
    const label = dateObj.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    if (segments.length === 0 || segments[segments.length - 1].label !== label) {
      segments.push({ label, span: 1 });
    } else {
      segments[segments.length - 1].span += 1;
    }
    return segments;
  }, []);

  const statsWeekSegments = [];
  statsWeekDates.forEach(day => {
    const weekNumber = getWeekNumber(new Date(day.date));
    if (!statsWeekSegments.length || statsWeekSegments[statsWeekSegments.length - 1].weekNumber !== weekNumber) {
      statsWeekSegments.push({ weekNumber, label: `KW ${weekNumber}`, span: 1 });
    } else {
      statsWeekSegments[statsWeekSegments.length - 1].span += 1;
    }
  });

  const statsEntriesByDate = useMemo(() => {
    const grouped = {};
    (statsData?.entries || []).forEach((entry) => {
      if (!entry?.datum) return;
      if (!grouped[entry.datum]) grouped[entry.datum] = [];
      grouped[entry.datum].push({
        ...entry,
        source: entry.source || 'report',
      });
    });

    (invoiceAssignments || []).forEach((invoice) => {
      if (!invoice?.date) return;
      if (!grouped[invoice.date]) grouped[invoice.date] = [];
      grouped[invoice.date].push({
        uid: `invoice-${invoice.id}`,
        source: 'invoice',
        datum: invoice.date,
        invoiceId: invoice.id,
        invoice_number: invoice.invoice_number || '',
        issuer: invoice.issuer || '',
        amount: invoice.amount,
        datei_name: invoice.datei_name || '',
      });
    });

    (materialAssignments || []).forEach((material) => {
      if (!material?.datum) return;
      if (!grouped[material.datum]) grouped[material.datum] = [];
      grouped[material.datum].push({
        uid: `material-${material.id}`,
        source: 'material',
        datum: material.datum,
        materialId: material.id,
        bezeichnung: material.bezeichnung || '',
        kategorie: material.kategorie || '',
        gesamtpreis: material.gesamtpreis,
        menge: material.menge,
        einheit: material.einheit || '',
      });
    });

    Object.keys(grouped).forEach((dateKey) => {
      grouped[dateKey].sort((a, b) => {
        if (a.source === b.source) {
          return String(a.uid || '').localeCompare(String(b.uid || ''));
        }
        const sourceOrder = { report: 0, manual: 0, material: 1, invoice: 2 };
        return (sourceOrder[a.source] || 9) - (sourceOrder[b.source] || 9);
      });
    });
    return grouped;
  }, [statsData, invoiceAssignments, materialAssignments]);

  const statsStartDateObj = new Date(statsWeekStart);
  const statsStartKW = getWeekNumber(statsStartDateObj);
  const statsEndKW = getWeekNumber(new Date(statsWeekDates[statsWeekDates.length - 1].date));
  const statsYear = statsStartDateObj.getFullYear();

  function changeStatsWeek(direction) {
    const current = new Date(statsWeekStart);
    current.setDate(current.getDate() + (direction * 7 * statsWeeksToShow));
    setStatsWeekStart(formatDate(current));
  }

  function getCurrentFormBaustelle() {
    return editingBaustelle || newBaustelle;
  }

  function resetManualHoursForm() {
    setManualHoursForm({
      id: null,
      datum: formatDate(new Date()),
      stunden: '',
      beschreibung: ''
    });
  }

  function resetManualInvoiceForm() {
    setManualInvoiceForm({
      datei_name: '',
      issuer: '',
      invoice_number: '',
      date: formatDate(new Date()),
      net_amount: '',
      tax: '',
      amount: '',
      bemerkung: '',
    });
  }

  function updateCurrentFormBaustelle(next) {
    if (editingBaustelle) {
      setEditingBaustelle(next);
    } else {
      setNewBaustelle(next);
    }
  }

  async function saveStundenverrechnungOverride(eintrag) {
    const currentBaustelleId = editingBaustelle?.id;
    if (!currentBaustelleId) {
      alert('Baustelle muss zuerst gespeichert werden.');
      return;
    }
    try {
      const payload = {
        baustelle: currentBaustelleId,
        wert: eintrag.wert,
        gueltig_ab: eintrag.gueltig_ab,
      };
      if (eintrag.id) {
        await axios.put(`${API_BASE_URL}/baustellen/stundenverrechnung-baustelle/${eintrag.id}/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_BASE_URL}/baustellen/stundenverrechnung-baustelle/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      setEditingStundenverrechnungOverride(null);
      setNewStundenverrechnungOverride(null);
      await loadStundenverrechnungOverrides(currentBaustelleId);
    } catch (error) {
      console.error('Fehler beim Speichern des Stundenverrechnung-Overrides:', error);
      alert(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Speichern des Overrides');
    }
  }

  async function deleteStundenverrechnungOverride(id) {
    if (!window.confirm('Override wirklich löschen?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/baustellen/stundenverrechnung-baustelle/${id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await loadStundenverrechnungOverrides(editingBaustelle?.id);
    } catch (error) {
      console.error('Fehler beim Löschen des Stundenverrechnung-Overrides:', error);
      alert('Fehler beim Löschen des Overrides');
    }
  }

  async function saveRechnungsaufschlagOverride(eintrag) {
    const currentBaustelleId = editingBaustelle?.id;
    if (!currentBaustelleId) {
      alert('Baustelle muss zuerst gespeichert werden.');
      return;
    }
    try {
      const payload = {
        baustelle: currentBaustelleId,
        wert: eintrag.wert,
        gueltig_ab: eintrag.gueltig_ab,
      };
      if (eintrag.id) {
        await axios.put(`${API_BASE_URL}/baustellen/rechnungsaufschlag-baustelle/${eintrag.id}/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_BASE_URL}/baustellen/rechnungsaufschlag-baustelle/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      setEditingRechnungsaufschlagOverride(null);
      setNewRechnungsaufschlagOverride(null);
      await loadRechnungsaufschlagOverrides(currentBaustelleId);
    } catch (error) {
      console.error('Fehler beim Speichern des Rechnungsaufschlag-Overrides:', error);
      alert(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Speichern des Overrides');
    }
  }

  async function deleteRechnungsaufschlagOverride(id) {
    if (!window.confirm('Override wirklich löschen?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/baustellen/rechnungsaufschlag-baustelle/${id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await loadRechnungsaufschlagOverrides(editingBaustelle?.id);
    } catch (error) {
      console.error('Fehler beim Löschen des Rechnungsaufschlag-Overrides:', error);
      alert('Fehler beim Löschen des Overrides');
    }
  }

  async function loadNextcloudDirectories(path = '') {
    setNextcloudLoading(true);
    setNextcloudError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/baustellen/nextcloud-directories/`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { path }
      });
      setNextcloudCurrentPath(response.data.current_path || '');
      setNextcloudDirs(response.data.directories || []);
    } catch (error) {
      console.error('Fehler beim Laden der Nextcloud-Verzeichnisse:', error);
      setNextcloudError(error.response?.data?.error || 'Fehler beim Laden der Verzeichnisse');
      setNextcloudDirs([]);
    }
    setNextcloudLoading(false);
  }

  async function openNextcloudPicker() {
    const current = getCurrentFormBaustelle();
    if (!current) return;
    setShowNextcloudPicker(true);
    await loadNextcloudDirectories(current.nextcloud_ordner_pfad || '');
  }

  function closeNextcloudPicker() {
    setShowNextcloudPicker(false);
    setNextcloudError('');
  }

  function goToNextcloudParent() {
    if (!nextcloudCurrentPath) return;
    const parts = nextcloudCurrentPath.split('/').filter(Boolean);
    parts.pop();
    loadNextcloudDirectories(parts.join('/'));
  }

  function selectNextcloudPath() {
    const current = getCurrentFormBaustelle();
    if (!current) return;
    updateCurrentFormBaustelle({
      ...current,
      nextcloud_ordner_pfad: nextcloudCurrentPath
    });
    closeNextcloudPicker();
  }

  function openCreateManualHours() {
    resetManualHoursForm();
    setShowManualHoursModal(true);
  }

  function openCreateManualInvoice() {
    resetManualInvoiceForm();
    setShowManualInvoiceModal(true);
  }

  function openEditManualHours(entry) {
    setManualHoursForm({
      id: entry.id,
      datum: entry.datum || formatDate(new Date()),
      stunden: String(entry.stunden ?? ''),
      beschreibung: entry.beschreibung || ''
    });
    setShowManualHoursModal(true);
  }

  function closeManualHoursModal() {
    setShowManualHoursModal(false);
    setManualHoursSaving(false);
    resetManualHoursForm();
  }

  function closeManualInvoiceModal() {
    setShowManualInvoiceModal(false);
    setManualInvoiceSaving(false);
    resetManualInvoiceForm();
  }

  async function saveManualHours() {
    if (!statsBaustelle?.id) return;
    const datum = (manualHoursForm.datum || '').trim();
    const stunden = parseFloat(String(manualHoursForm.stunden).replace(',', '.'));
    const beschreibung = (manualHoursForm.beschreibung || '').trim();

    if (!datum) {
      alert('Bitte ein Datum auswaehlen.');
      return;
    }
    if (Number.isNaN(stunden) || stunden <= 0) {
      alert('Bitte eine gueltige Stundenzahl > 0 eingeben.');
      return;
    }

    setManualHoursSaving(true);
    const payload = {
      baustelle: statsBaustelle.id,
      datum,
      stunden: stunden.toFixed(2),
      beschreibung
    };

    try {
      if (manualHoursForm.id) {
        await axios.put(
          `${API_BASE_URL}/baustellen/manuelle-stunden/${manualHoursForm.id}/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/baustellen/manuelle-stunden/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      closeManualHoursModal();
      await loadBaustelleStats(statsBaustelle, { preserveView: true });
    } catch (error) {
      console.error('Fehler beim Speichern der manuellen Stunden:', error);
      alert('Fehler beim Speichern der manuellen Stunden');
      setManualHoursSaving(false);
    }
  }

  async function saveManualInvoice() {
    if (!statsBaustelle?.id) return;
    const payload = {
      baustelle: statsBaustelle.id,
      datei_name: (manualInvoiceForm.datei_name || '').trim() || 'manuell-eingepflegte-rechnung',
      quell_pfad: '',
      nextcloud_pfad: '',
      issuer: (manualInvoiceForm.issuer || '').trim(),
      invoice_number: (manualInvoiceForm.invoice_number || '').trim(),
      date: (manualInvoiceForm.date || '').trim() || null,
      due_date: null,
      amount: manualInvoiceForm.amount === '' ? null : String(manualInvoiceForm.amount).replace(',', '.'),
      net_amount: manualInvoiceForm.net_amount === '' ? null : String(manualInvoiceForm.net_amount).replace(',', '.'),
      tax: manualInvoiceForm.tax === '' ? null : String(manualInvoiceForm.tax).replace(',', '.'),
      tax_rate: null,
      currency: 'EUR',
      customer_number: '',
      project: '',
      is_credit_note: false,
      analysis_payload: {
        manual_entry: true,
        bemerkung: manualInvoiceForm.bemerkung || '',
      },
    };

    if (!payload.issuer) {
      alert('Bitte einen Lieferanten eingeben.');
      return;
    }
    if (!payload.date) {
      alert('Bitte ein Rechnungsdatum eingeben.');
      return;
    }

    setManualInvoiceSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/baustellen/rechnungen/`, payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      closeManualInvoiceModal();
      await refreshInvoiceAssignments();
    } catch (error) {
      console.error('Fehler beim Speichern der manuellen Rechnung:', error);
      alert(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Speichern der manuellen Rechnung');
      setManualInvoiceSaving(false);
    }
  }

  async function deleteManualHours(entry) {
    if (!window.confirm('Diesen manuellen Eintrag wirklich loeschen?')) {
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/baustellen/manuelle-stunden/${entry.id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await loadBaustelleStats(statsBaustelle, { preserveView: true });
    } catch (error) {
      console.error('Fehler beim Loeschen der manuellen Stunden:', error);
      alert('Fehler beim Loeschen der manuellen Stunden');
    }
  }

  async function refreshInvoiceAssignments() {
    if (!statsBaustelle?.id) return;
    setInvoiceAssignmentsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/rechnungen/`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { baustelle: statsBaustelle.id, page_size: 500 }
      });
      const invoiceResults = response.data.results || response.data || [];
      setInvoiceAssignments(Array.isArray(invoiceResults) ? invoiceResults : []);
    } catch (error) {
      console.error('Fehler beim Laden der Rechnungszuordnungen:', error);
      alert('Fehler beim Laden der Rechnungszuordnungen');
    }
    setInvoiceAssignmentsLoading(false);
  }

  async function refreshMaterialAssignments() {
    if (!statsBaustelle?.id) return;
    setMaterialAssignmentsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/kostenpositionen/`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { baustelle: statsBaustelle.id, page_size: 500 }
      });
      const results = response.data.results || response.data || [];
      setMaterialAssignments(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Fehler beim Laden der Materialbuchungen:', error);
      alert('Fehler beim Laden der Materialbuchungen');
    }
    setMaterialAssignmentsLoading(false);
  }

  function exportMasterListAsExcel() {
    if (!statsBaustelle || masterListData.length === 0) {
      alert('Keine Daten für den Export vorhanden.');
      return;
    }

    const rows = [[
      'Datum',
      'Stunden',
      'Rechnungen / Material',
      'Tages-/Monatssumme',
      'Aktuelle Gesamtsumme',
    ]];
    masterListData.forEach((row) => {
      if (row.type === 'summary') {
        rows.push([
          row.label,
          'Monatssumme',
          '',
          formatAmountDe(row.monthSum),
          formatAmountDe(row.cumulative),
        ]);
        return;
      }

      const hourText = row.hoursEntries.map((entry) =>
        `${entry.mitarbeiterName || 'Unbekannt'}: ${entry.stunden}h @ ${formatAmountDe(entry.rate)}`
      ).join(' | ');
      const invoiceText = row.invoiceEntries.map((entry) =>
        `${entry.invoice_number || '-'} ${entry.issuer || ''} ${formatAmountDe(entry.amount)} EUR`
      ).join(' | ');
      const materialText = row.materialEntries.map((entry) =>
        `${entry.bezeichnung || '-'} ${formatAmountDe(entry.amount)} EUR`
      ).join(' | ');

      rows.push([
        formatMasterDate(row.date),
        hourText,
        invoiceText + (invoiceText && materialText ? ' || ' : '') + materialText,
        formatAmountDe(row.total),
        '',
      ]);
    });

    downloadOds(
      `Masterliste_${(statsBaustelle.nummer || 'Baustelle').replace(/\s+/g, '_')}.ods`,
      rows,
      'Masterliste'
    );
  }

  function confirmMasterInvoiceSurcharge() {
    const normalized = String(masterInvoiceSurchargeInput || '').trim().replace(',', '.');
    const parsed = Number(normalized);
    if (Number.isNaN(parsed)) {
      alert('Bitte einen gültigen Prozentwert eingeben.');
      return;
    }
    setMasterInvoiceSurcharge(parsed);
    setMasterInvoiceSurchargeInput(String(parsed).replace('.', ','));
  }

  async function openOriginalInvoice(rechnungId) {
    if (!rechnungId) return;
    setInvoiceFileLoadingId(rechnungId);
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/rechnungen/${rechnungId}/file-content/`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      console.error('Fehler beim Laden der Originalrechnung:', error);
      alert(error.response?.data?.error || 'Fehler beim Laden der Originalrechnung');
    }
    setInvoiceFileLoadingId(null);
  }

  function startEditPosition(position, rechnung) {
    setPositionEditing({
      id: position.id,
      rechnungId: rechnung.id,
      invoiceLabel: `${rechnung.invoice_number || '-'} / ${rechnung.issuer || '-'}`,
      article_no: position.article_no || '',
      description: position.description || '',
      quantity: position.quantity ?? '',
      unit: position.unit || '',
      unit_price: position.unit_price ?? '',
      line_total: position.line_total ?? '',
    });
  }

  function closePositionEdit() {
    setPositionEditing(null);
    setPositionSaving(false);
  }

  async function savePositionEdit() {
    if (!positionEditing?.id) return;
    setPositionSaving(true);
    const toNumberOrNull = (value) => {
      if (value === '' || value === null || value === undefined) return null;
      const n = Number(String(value).replace(',', '.'));
      return Number.isNaN(n) ? null : n;
    };
    const payload = {
      article_no: positionEditing.article_no || '',
      description: positionEditing.description || '',
      quantity: toNumberOrNull(positionEditing.quantity),
      unit: positionEditing.unit || '',
      unit_price: toNumberOrNull(positionEditing.unit_price),
      line_total: toNumberOrNull(positionEditing.line_total),
    };
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/baustellen/rechnungspositionen/${positionEditing.id}/`,
        payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const updated = response.data;
      setInvoiceAssignments((prev) => prev.map((inv) => ({
        ...inv,
        positionen: (inv.positionen || []).map((pos) => (pos.id === updated.id ? { ...pos, ...updated } : pos))
      })));
      closePositionEdit();
    } catch (error) {
      console.error('Fehler beim Speichern der Position:', error);
      alert(error.response?.data?.error || 'Fehler beim Speichern der Position');
      setPositionSaving(false);
    }
  }

  async function deletePosition(positionId) {
    if (!window.confirm('Diese Rechnungsposition wirklich löschen?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/baustellen/rechnungspositionen/${positionId}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setInvoiceAssignments((prev) => prev.map((inv) => ({
        ...inv,
        positionen: (inv.positionen || []).filter((pos) => pos.id !== positionId),
      })));
    } catch (error) {
      console.error('Fehler beim Löschen der Position:', error);
      alert(error.response?.data?.error || 'Fehler beim Löschen der Position');
    }
  }

  function openInvoiceDeleteDialog(invoice) {
    setInvoiceDeleteDialog({
      open: true,
      invoice,
      fileAction: 'move_to_inbox',
      submitting: false,
    });
  }

  function closeInvoiceDeleteDialog() {
    setInvoiceDeleteDialog({
      open: false,
      invoice: null,
      fileAction: 'move_to_inbox',
      submitting: false,
    });
  }

  async function confirmDeleteInvoice() {
    if (!invoiceDeleteDialog?.invoice?.id) return;
    const invoiceLabel = `${invoiceDeleteDialog.invoice.invoice_number || '-'} / ${invoiceDeleteDialog.invoice.issuer || '-'}`;
    const actionLabel = invoiceDeleteDialog.fileAction === 'delete'
      ? 'Datei in Nextcloud löschen'
      : 'Datei in den allgemeinen Rechnungseingangsordner verschieben';
    const firstConfirm = window.confirm(
      `Rechnung ${invoiceLabel} wirklich löschen?\n\nAktion für die Datei: ${actionLabel}\n\nDie Rechnung inkl. aller Positionen wird aus der Baustellen-Datenbank entfernt.`
    );
    if (!firstConfirm) return;

    const secondConfirm = window.confirm(
      'Sicherheitsabfrage: Wenn die Datei gelöscht wird, kann die Rechnung vollständig aus dem System verschwinden. Wirklich fortfahren?'
    );
    if (!secondConfirm) return;

    setInvoiceDeleteDialog((prev) => ({ ...prev, submitting: true }));
    try {
      const response = await axios.post(
        `${API_BASE_URL}/baustellen/rechnungen/${invoiceDeleteDialog.invoice.id}/delete-with-file/`,
        { file_action: invoiceDeleteDialog.fileAction },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const deletedId = response.data?.deleted_invoice_id || invoiceDeleteDialog.invoice.id;
      setInvoiceAssignments((prev) => prev.filter((inv) => inv.id !== deletedId));
      closeInvoiceDeleteDialog();
      alert('Rechnung wurde gelöscht.');
    } catch (error) {
      console.error('Fehler beim Löschen der Rechnung:', error);
      alert(error.response?.data?.error || 'Fehler beim Löschen der Rechnung');
      setInvoiceDeleteDialog((prev) => ({ ...prev, submitting: false }));
    }
  }

  function toggleSort(field) {
    setSortConfig((prev) => (
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' }
    ));
  }

  function renderSortLabel(label, field) {
    const active = sortConfig.field === field;
    const arrow = active ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';
    return `${label}${arrow}`;
  }

  async function openMasterListShortcut(baustelle) {
    await loadBaustelleStats(baustelle);
    setShowMasterList(true);
  }

  async function loadImportDirectories(path = '') {
    if (!importWizardBaustelle?.id) return;
    setImportWizardLoading(true);
    setImportWizardError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/baustellen/nextcloud-directories/`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { path }
      });
      setImportWizardPath(response.data.current_path || '');
      setImportWizardDirs(response.data.directories || []);
    } catch (error) {
      setImportWizardError(error.response?.data?.error || 'Fehler beim Laden der Nextcloud-Verzeichnisse');
      setImportWizardDirs([]);
    }
    setImportWizardLoading(false);
  }

  async function refreshImportFiles(baustelle = importWizardBaustelle) {
    if (!baustelle?.id) return;
    setImportWizardLoading(true);
    setImportWizardError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/baustellen/${baustelle.id}/nextcloud-import-files/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setImportWizardFolderExists(true);
      setImportWizardFiles(response.data.files || []);
      setImportWizardStep(3);
    } catch (error) {
      setImportWizardFolderExists(false);
      setImportWizardFiles([]);
      setImportWizardError(error.response?.data?.error || 'Fehler beim Laden des Ordners Eingangsrechnungen');
      setImportWizardStep(2);
    }
    setImportWizardLoading(false);
  }

  async function openImportWizard(baustelle) {
    setImportWizardBaustelle(baustelle);
    setShowImportWizard(true);
    setImportWizardError('');
    setImportWizardFiles([]);
    setSelectedImportFile('');
    setImportPreviewRows([]);
    setSelectedImportRows([]);
    if (baustelle?.nextcloud_ordner_pfad) {
      setImportWizardStep(2);
      await refreshImportFiles(baustelle);
    } else {
      setImportWizardStep(1);
      await loadImportDirectories('');
    }
  }

  function closeImportWizard() {
    setShowImportWizard(false);
    setImportWizardBaustelle(null);
    setImportWizardStep(1);
    setImportWizardLoading(false);
    setImportWizardError('');
    setImportWizardPath('');
    setImportWizardDirs([]);
    setImportWizardFolderExists(false);
    setImportWizardFiles([]);
    setSelectedImportFile('');
    setImportPreviewRows([]);
    setSelectedImportRows([]);
  }

  async function saveImportWizardPath() {
    if (!importWizardBaustelle?.id || !importWizardPath) return;
    setImportWizardLoading(true);
    setImportWizardError('');
    try {
      await axios.patch(`${API_BASE_URL}/baustellen/baustellen/${importWizardBaustelle.id}/`, {
        nextcloud_ordner_pfad: importWizardPath
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const updated = { ...importWizardBaustelle, nextcloud_ordner_pfad: importWizardPath };
      setImportWizardBaustelle(updated);
      setBaustellen((prev) => prev.map((entry) => entry.id === updated.id ? { ...entry, nextcloud_ordner_pfad: importWizardPath } : entry));
      setImportWizardStep(2);
      await refreshImportFiles(updated);
    } catch (error) {
      setImportWizardError(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Speichern des Nextcloud-Ordners');
      setImportWizardLoading(false);
    }
  }

  async function ensureImportFolder() {
    if (!importWizardBaustelle?.id) return;
    setImportWizardLoading(true);
    setImportWizardError('');
    try {
      await axios.post(`${API_BASE_URL}/baustellen/baustellen/${importWizardBaustelle.id}/ensure-nextcloud-subdir/`, {
        subdir: 'Eingangsrechnungen'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await refreshImportFiles(importWizardBaustelle);
    } catch (error) {
      setImportWizardError(error.response?.data?.error || 'Fehler beim Erstellen des Ordners Eingangsrechnungen');
      setImportWizardLoading(false);
    }
  }

  function parseImportDate(value) {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return formatDate(value);
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      const parsed = XLSX.SSF.parse_date_code(value);
      if (parsed && parsed.y && parsed.m && parsed.d) {
        return `${String(parsed.y).padStart(4, '0')}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
      }
    }
    const raw = String(value || '').trim();
    if (!raw) return '';
    const normalized = raw.replace(/\s+/g, '');
    const match = normalized.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
    if (match) {
      const year = match[3].length === 2 ? `20${match[3]}` : match[3];
      return `${year}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
    }
    const iso = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) return normalized;
    const slash = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (slash) {
      const year = slash[3].length === 2 ? `20${slash[3]}` : slash[3];
      return `${year}-${slash[1].padStart(2, '0')}-${slash[2].padStart(2, '0')}`;
    }
    return '';
  }

  function formatImportPreviewDate(value) {
    const parsed = parseImportDate(value);
    if (!parsed) return String(value || '');
    const [year, month, day] = parsed.split('-');
    if (!year || !month || !day) return String(value || '');
    return `${day}.${month}.${year}`;
  }

  function formatImportPreviewAmount(value) {
    const parsed = parseImportNumber(value);
    if (parsed === null) return String(value || '');
    return `${parsed.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }

  function parseImportNumber(value) {
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    const cleaned = raw
      .replace(/\u00a0/g, ' ')
      .replace(/[€$£]/g, '')
      .replace(/[^0-9,.\-]/g, '')
      .replace(/\s/g, '');
    if (!cleaned) return null;

    let normalized = cleaned;
    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');

    if (hasComma && hasDot) {
      const lastComma = cleaned.lastIndexOf(',');
      const lastDot = cleaned.lastIndexOf('.');
      if (lastComma > lastDot) {
        normalized = cleaned.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = cleaned.replace(/,/g, '');
      }
    } else if (hasComma) {
      const commaParts = cleaned.split(',');
      normalized = commaParts.length === 2
        ? `${commaParts[0].replace(/\./g, '')}.${commaParts[1]}`
        : cleaned.replace(/\./g, '').replace(/,/g, '');
    } else if (hasDot) {
      const dotParts = cleaned.split('.');
      normalized = dotParts.length === 2
        ? `${dotParts[0].replace(/,/g, '')}.${dotParts[1]}`
        : cleaned.replace(/\./g, '');
    }

    const num = Number(normalized);
    return Number.isNaN(num) ? null : num;
  }

  function mapImportedRowToInvoice(row, fileName) {
    const issuer = String(row.col_a || '').trim();
    const date = parseImportDate(row.col_b) || formatDate(new Date());
    const bemerkung = String(row.col_c || '').trim();
    const netAmount = parseImportNumber(row.col_d);
    const taxAmount = parseImportNumber(row.col_e);
    const grossAmount = parseImportNumber(row.col_f);
    const invoiceNumberParts = [issuer, date].filter(Boolean);
    return {
      baustelle: importWizardBaustelle.id,
      datei_name: fileName || 'importierte-rechnung',
      quell_pfad: `${importWizardBaustelle?.nextcloud_ordner_pfad || ''}/Eingangsrechnungen/${fileName}`.replace(/^\/+/, ''),
      nextcloud_pfad: '',
      issuer: issuer || 'Unbekannter Lieferant',
      invoice_number: invoiceNumberParts.join(' - '),
      date,
      due_date: null,
      amount: grossAmount !== null ? String(grossAmount.toFixed(2)) : '',
      net_amount: netAmount !== null ? String(netAmount.toFixed(2)) : '',
      tax: taxAmount !== null ? String(taxAmount.toFixed(2)) : '',
      tax_rate: '',
      currency: 'EUR',
      customer_number: '',
      project: bemerkung,
      is_credit_note: grossAmount !== null && grossAmount < 0,
      analysis_payload: {
        import_source: 'kostenimport_assistent',
        import_file: fileName || '',
        import_remarks: bemerkung,
        import_row: {
          a: row.col_a || '',
          b: row.col_b || '',
          c: row.col_c || '',
          d: row.col_d || '',
          e: row.col_e || '',
          f: row.col_f || '',
        },
      },
    };
  }

  async function loadImportPreview() {
    if (!importWizardBaustelle?.id || !selectedImportFile) return;
    setImportWizardLoading(true);
    setImportWizardError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/baustellen/${importWizardBaustelle.id}/nextcloud-import-file-content/`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { path: `Eingangsrechnungen/${selectedImportFile}` },
        responseType: 'arraybuffer'
      });
      const workbook = XLSX.read(response.data, { type: 'array', cellDates: true });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, raw: true, defval: '' });
      const startIndex = rows.findIndex((row) => String(row?.[0] || '').trim().toLowerCase() === 'lieferant');
      if (startIndex < 0) {
        throw new Error('In der ersten Spalte wurde kein Eintrag "Lieferant" gefunden.');
      }
      const preview = rows.slice(startIndex + 1)
        .map((row, index) => ({
          id: index,
          col_a: row?.[0] || '',
          col_b: row?.[1] || '',
          col_c: row?.[2] || '',
          col_d: row?.[3] || '',
          col_e: row?.[4] || '',
          col_f: row?.[5] || '',
        }))
        .filter((row) => String(row.col_a || '').trim())
        .filter((row) => parseImportDate(row.col_b) || parseImportNumber(row.col_f) !== null || parseImportNumber(row.col_d) !== null);
      setImportPreviewRows(preview);
      setSelectedImportRows(preview.map((row) => row.id));
      setImportWizardStep(4);
    } catch (error) {
      setImportWizardError(error.message || error.response?.data?.error || 'Fehler beim Einlesen der Tabelle');
    }
    setImportWizardLoading(false);
  }

  async function importSelectedRows() {
    if (!importWizardBaustelle?.id || selectedImportRows.length === 0) {
      alert('Bitte mindestens einen Datensatz auswählen.');
      return;
    }
    setImportWizardLoading(true);
    setImportWizardError('');
    try {
      const selectedRows = importPreviewRows.filter((row) => selectedImportRows.includes(row.id));
      for (const row of selectedRows) {
        const payload = mapImportedRowToInvoice(row, selectedImportFile);
        await axios.post(`${API_BASE_URL}/baustellen/rechnungen/`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      if (statsBaustelle?.id === importWizardBaustelle.id) {
        await refreshInvoiceAssignments();
      }
      alert(`${selectedRows.length} Datensätze importiert.`);
      closeImportWizard();
    } catch (error) {
      setImportWizardError(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Import der Rechnungen');
      setImportWizardLoading(false);
      return;
    }
    setImportWizardLoading(false);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Baustellen</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={loadBaustellen} className="btn" disabled={loading}>
            <RefreshCw size={18} className={loading ? 'spinning' : ''} /> Aktualisieren
          </button>
          {!newBaustelle && !editingBaustelle && (
            <button
              onClick={() => setNewBaustelle({ ...DEFAULT_BAUSTELLE })}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Plus size={18} /> Neue Baustelle
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', alignItems: 'center' }}>
          <input
            type="text"
            className="input"
            placeholder="Suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Alle Status</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
      </div>

      {!statsBaustelle && (newBaustelle || editingBaustelle) && (
        <div ref={formContainerRef}>
          <BaustelleForm
            baustelle={editingBaustelle || newBaustelle}
            isEditing={Boolean(editingBaustelle)}
            stundenverrechnungOverrides={stundenverrechnungOverrides}
            editingStundenverrechnungOverride={editingStundenverrechnungOverride}
            newStundenverrechnungOverride={newStundenverrechnungOverride}
            rechnungsaufschlagOverrides={rechnungsaufschlagOverrides}
            editingRechnungsaufschlagOverride={editingRechnungsaufschlagOverride}
            newRechnungsaufschlagOverride={newRechnungsaufschlagOverride}
            onChange={updateCurrentFormBaustelle}
            onSave={() => saveBaustelle(editingBaustelle || newBaustelle)}
            onCancel={() => {
              setEditingBaustelle(null);
              setNewBaustelle(null);
              setEditingStundenverrechnungOverride(null);
              setNewStundenverrechnungOverride(null);
              setStundenverrechnungOverrides([]);
              setEditingRechnungsaufschlagOverride(null);
              setNewRechnungsaufschlagOverride(null);
              setRechnungsaufschlagOverrides([]);
              closeNextcloudPicker();
            }}
            onConnectNextcloud={openNextcloudPicker}
            onStartNewStundenverrechnungOverride={() => setNewStundenverrechnungOverride({
              baustelle: editingBaustelle?.id,
              wert: '',
              gueltig_ab: new Date().toISOString().slice(0, 10),
            })}
            onEditStundenverrechnungOverride={(eintrag) => setEditingStundenverrechnungOverride({ ...eintrag })}
            onSaveStundenverrechnungOverride={saveStundenverrechnungOverride}
            onCancelStundenverrechnungOverride={() => {
              setEditingStundenverrechnungOverride(null);
              setNewStundenverrechnungOverride(null);
            }}
            onChangeNewStundenverrechnungOverride={setNewStundenverrechnungOverride}
            onChangeEditingStundenverrechnungOverride={setEditingStundenverrechnungOverride}
            onDeleteStundenverrechnungOverride={deleteStundenverrechnungOverride}
            onStartNewRechnungsaufschlagOverride={() => setNewRechnungsaufschlagOverride({
              baustelle: editingBaustelle?.id,
              wert: '',
              gueltig_ab: new Date().toISOString().slice(0, 10),
            })}
            onEditRechnungsaufschlagOverride={(eintrag) => setEditingRechnungsaufschlagOverride({ ...eintrag })}
            onSaveRechnungsaufschlagOverride={saveRechnungsaufschlagOverride}
            onCancelRechnungsaufschlagOverride={() => {
              setEditingRechnungsaufschlagOverride(null);
              setNewRechnungsaufschlagOverride(null);
            }}
            onChangeNewRechnungsaufschlagOverride={setNewRechnungsaufschlagOverride}
            onChangeEditingRechnungsaufschlagOverride={setEditingRechnungsaufschlagOverride}
            onDeleteRechnungsaufschlagOverride={deleteRechnungsaufschlagOverride}
          />
        </div>
      )}

      <div className="card">
        {statsBaustelle && (
          <div style={{ marginBottom: '1.5rem' }}>
            <button
              className="btn"
              onClick={() => {
                setStatsBaustelle(null);
                setStatsData(null);
                setShowDetails(false);
                setShowInvoiceDetails(false);
                setShowMaterialDetails(false);
                setShowMasterList(false);
              }}
            >
              Zurück zur Liste
            </button>
          </div>
        )}

        {statsBaustelle && (
          <div className="card" style={{ marginBottom: '1.5rem', background: '#f9fafb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Statistiken: {statsBaustelle.nummer || '-'} {statsBaustelle.name}
                </h3>
                <p style={{ color: '#6b7280', margin: 0 }}>
                  {statsBaustelle.kunde_name || ''}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button className="btn" onClick={() => startEditBaustelle(statsBaustelle, { openFromDetails: true })}>
                  <Edit3 size={16} /> Bearbeiten
                </button>
              </div>
            </div>

            {statsLoading ? (
              <div style={{ color: '#6b7280' }}>Lade Statistiken...</div>
            ) : statsData ? (
              <>
                <div style={{ marginBottom: '1rem', fontWeight: '600' }}>
                  Summe: {statsData.totalHours}h
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      onClick={() => changeStatsWeek(-1)}
                      className="btn"
                    >
                      Vorherige
                    </button>

                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: '600', fontSize: '1rem' }}>
                        KW {statsStartKW}{statsWeeksToShow > 1 ? ` - ${statsEndKW}` : ''} / {statsYear}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {statsWeekDates[0].dayNumber}. {statsWeekDates[0].month} - {statsWeekDates[statsWeekDates.length - 1].dayNumber}. {statsWeekDates[statsWeekDates.length - 1].month}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => setStatsWeeksToShow(prev => Math.max(1, prev - 1))}
                        className="btn"
                      >
                        Weniger Wochen anzeigen
                      </button>
                      <button
                        onClick={() => {
                          const stored = localStorage.getItem('baustellenStatsDefaultWeeks');
                          const parsed = stored ? parseInt(stored, 10) : 2;
                          setStatsWeeksToShow(Number.isNaN(parsed) ? 2 : Math.max(1, parsed));
                        }}
                        className="btn"
                      >
                        Normale Anzeige
                      </button>
                      <button
                        onClick={() => setStatsWeeksToShow(prev => prev + 1)}
                        className="btn"
                      >
                        Mehr Wochen anzeigen
                      </button>
                      <button
                        onClick={() => changeStatsWeek(1)}
                        className="btn"
                      >
                        Nächste
                      </button>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }}>
                        <input
                          type="checkbox"
                          checked={statsShowPerson}
                          onChange={(e) => {
                            setStatsShowPerson(e.target.checked);
                            localStorage.setItem('baustellenStatsShowPerson', String(e.target.checked));
                          }}
                          style={{ width: '1.05rem', height: '1.05rem', cursor: 'pointer' }}
                        />
                        Person anzeigen
                      </label>
                    </div>
                  </div>

                  <div style={{ marginTop: '1rem' }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${statsWeekDates.length}, 1fr)`,
                      gap: '0.5rem',
                      marginBottom: '0.5rem'
                    }}>
                      {statsMonthSegments.map((segment, index) => (
                        <div
                          key={`stats-month-${index}`}
                          style={{
                            gridColumn: `span ${segment.span}`,
                            background: '#eef2ff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            padding: '0.4rem',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            textAlign: 'center',
                            color: '#1f2937'
                          }}
                        >
                          {segment.label}
                        </div>
                      ))}
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${statsWeekDates.length}, 1fr)`,
                      gap: '0.5rem',
                      marginBottom: '0.5rem'
                    }}>
                      {statsWeekSegments.map((segment, index) => (
                        <div
                          key={`stats-week-${index}`}
                          style={{
                            gridColumn: `span ${segment.span}`,
                            background: '#f3f4f6',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            padding: '0.35rem',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            textAlign: 'center',
                            color: '#374151'
                          }}
                        >
                          {segment.label}
                        </div>
                      ))}
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${statsWeekDates.length}, 1fr)`,
                      gap: '0.5rem',
                      marginBottom: '0.5rem'
                    }}>
                      {statsWeekDates.map(day => (
                        <div
                          key={`stats-day-${day.date}`}
                          style={{
                            background: day.isToday ? '#eff6ff' : '#f9fafb',
                            border: day.isToday ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            padding: '0.4rem',
                            minHeight: '70px'
                          }}
                        >
                          <div style={{
                            fontWeight: '600',
                            fontSize: '0.85rem',
                            color: day.isToday ? '#1e40af' : '#374151',
                            marginBottom: '0.2rem'
                          }}>
                            {day.dayName}
                          </div>
                          <div style={{
                            fontSize: '1.15rem',
                            fontWeight: 'bold',
                            color: day.isToday ? '#1e40af' : '#111827'
                          }}>
                            {day.dayNumber}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${statsWeekDates.length}, 1fr)`,
                      gap: '0.5rem'
                    }}>
                      {statsWeekDates.map(day => {
                        const dayEntries = statsEntriesByDate[day.date] || [];
                        const bgColor = statsBaustelle?.farbe || '#3B82F6';
                        const textColor = getContrastTextColor(bgColor);
                        return (
                          <div
                            key={`stats-events-${day.date}`}
                            style={{
                              background: '#ffffff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.5rem',
                              padding: '0.35rem',
                              minHeight: '60px'
                            }}
                          >
                            {dayEntries.length === 0 ? (
                              <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>-</div>
                            ) : (
                              <div style={{ display: 'grid', gap: '0.25rem' }}>
                                {dayEntries.map(entry => {
                                  if (entry.source === 'invoice') {
                                    return (
                                      <div
                                        key={entry.uid}
                                        style={{
                                          background: '#0f766e',
                                          color: '#ffffff',
                                          borderRadius: '0.35rem',
                                          padding: '0.2rem 0.35rem',
                                          fontSize: '0.7rem',
                                          fontWeight: '600'
                                        }}
                                        title={`Rechnung ${entry.invoice_number || '-'} • ${entry.issuer || '-'} • ${formatAmountDe(entry.amount || 0)} EUR`}
                                      >
                                        R: {entry.invoice_number || '-'} • {formatAmountDe(entry.amount || 0)} EUR
                                      </div>
                                    );
                                  }
                                  if (entry.source === 'material') {
                                    return (
                                      <div
                                        key={entry.uid}
                                        style={{
                                          background: '#7c3aed',
                                          color: '#ffffff',
                                          borderRadius: '0.35rem',
                                          padding: '0.2rem 0.35rem',
                                          fontSize: '0.7rem',
                                          fontWeight: '600'
                                        }}
                                        title={`${entry.bezeichnung || '-'} • ${entry.menge || '-'} ${entry.einheit || ''} • ${formatAmountDe(entry.gesamtpreis || 0)} EUR`}
                                      >
                                        M: {entry.bezeichnung || '-'} • {formatAmountDe(entry.gesamtpreis || 0)} EUR
                                      </div>
                                    );
                                  }
                                  return (
                                    <div
                                      key={entry.uid}
                                      style={{
                                        background: bgColor,
                                        color: textColor,
                                        borderRadius: '0.35rem',
                                        padding: '0.2rem 0.35rem',
                                        fontSize: '0.7rem',
                                        fontWeight: '600'
                                      }}
                                      title={entry.beschreibung || `${entry.stunden}h`}
                                    >
                                      {statsShowPerson && entry.mitarbeiterName ? `${entry.mitarbeiterName} • ` : ''}
                                      {entry.beschreibung ? `${entry.stunden}h • ${entry.beschreibung}` : `${entry.stunden}h`}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    className="btn"
                    onClick={() => setShowDetails(prev => !prev)}
                  >
                    {showDetails ? 'Details ausblenden' : 'Stundenliste anzeigen'}
                  </button>
                  <button
                    className="btn"
                    onClick={() => setShowInvoiceDetails(prev => !prev)}
                  >
                    {showInvoiceDetails ? 'Rechnungsliste ausblenden' : 'Rechnungsliste anzeigen'}
                  </button>
                  <button
                    className="btn"
                    onClick={() => setShowMaterialDetails(prev => !prev)}
                  >
                    {showMaterialDetails ? 'Materialliste ausblenden' : 'Materialliste anzeigen'}
                  </button>
                  <button
                    className="btn"
                    onClick={() => setShowMasterList(prev => !prev)}
                  >
                    {showMasterList ? 'Masterliste ausblenden' : 'Masterliste'}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={openCreateManualHours}
                  >
                    Stunden manuell hinzufügen
                  </button>
                </div>

                {showMasterList && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                      <h4 style={{ margin: 0, fontSize: '1.05rem' }}>Masterliste</h4>
                      <button className="btn" onClick={exportMasterListAsExcel}>
                        Als Excel exportieren
                      </button>
                    </div>

                    <div className="card" style={{ margin: '0 0 0.75rem 0', background: '#f9fafb' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '0.75rem', alignItems: 'end' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <input
                            type="checkbox"
                            checked={masterFilters.stunden}
                            onChange={(e) => setMasterFilters((prev) => ({ ...prev, stunden: e.target.checked }))}
                          />
                          <span>Stunden</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <input
                            type="checkbox"
                            checked={masterFilters.rechnungen}
                            onChange={(e) => setMasterFilters((prev) => ({ ...prev, rechnungen: e.target.checked }))}
                          />
                          <span>Rechnungen</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <input
                            type="checkbox"
                            checked={masterFilters.material}
                            onChange={(e) => setMasterFilters((prev) => ({ ...prev, material: e.target.checked }))}
                          />
                          <span>Material</span>
                        </label>
                        <label style={{ display: 'grid', gap: '0.25rem' }}>
                          <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Startdatum</span>
                          <input
                            type="date"
                            className="input"
                            value={masterFilters.startDate}
                            onChange={(e) => setMasterFilters((prev) => ({ ...prev, startDate: e.target.value }))}
                          />
                        </label>
                        <label style={{ display: 'grid', gap: '0.25rem' }}>
                          <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Enddatum</span>
                          <input
                            type="date"
                            className="input"
                            value={masterFilters.endDate}
                            onChange={(e) => setMasterFilters((prev) => ({ ...prev, endDate: e.target.value }))}
                          />
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'end', marginTop: '0.75rem' }}>
                        <label style={{ display: 'grid', gap: '0.25rem', minWidth: '220px' }}>
                          <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Rechnungs-Aufschlag in %</span>
                          <input
                            type="text"
                            className="input"
                            value={masterInvoiceSurchargeInput}
                            onChange={(e) => setMasterInvoiceSurchargeInput(e.target.value)}
                            placeholder="z. B. 10"
                          />
                        </label>
                        <button className="btn" onClick={confirmMasterInvoiceSurcharge}>
                          Aufschlag einrechnen
                        </button>
                        <div style={{ color: '#374151', fontSize: '0.9rem' }}>
                          Aktiver Aufschlag: <strong>{formatAmountDe(masterInvoiceSurcharge)} %</strong>
                        </div>
                      </div>
                    </div>

                    {masterListData.length === 0 ? (
                      <div style={{ color: '#6b7280' }}>Keine Daten für die aktuelle Filterung vorhanden.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {masterListData.map((row) => (
                          row.type === 'summary' ? (
                            <div
                              key={row.key}
                              className="card"
                              style={{ margin: 0, background: '#eef2ff', border: '1px solid #c7d2fe' }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', fontWeight: 700 }}>
                                <span>{row.label}</span>
                                <span>Monatssumme: {formatAmountDe(row.monthSum)} EUR</span>
                                <span>Gesamtsumme bis hier: {formatAmountDe(row.cumulative)} EUR</span>
                              </div>
                            </div>
                          ) : (
                            <div key={row.key} className="card" style={{ margin: 0, background: '#fff' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                                <div style={{ fontWeight: 700 }}>{formatMasterDate(row.date)}</div>
                                <div style={{ fontWeight: 700 }}>Tagessumme: {formatAmountDe(row.total)} EUR</div>
                              </div>

                              {row.hoursEntries.length > 0 && (
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Stunden</div>
                                  <div style={{ display: 'grid', gap: '0.25rem' }}>
                                    {row.hoursEntries.map((entry) => (
                                      <div key={entry.uid} style={{ fontSize: '0.9rem', color: '#374151' }}>
                                        {entry.mitarbeiterName} • {entry.stunden}h • {entry.beschreibung || '-'} • {formatAmountDe(entry.rate)} EUR/h = <strong>{formatAmountDe(entry.cost)} EUR</strong>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {row.invoiceEntries.length > 0 && (
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Rechnungen</div>
                                  <div style={{ display: 'grid', gap: '0.25rem' }}>
                                    {row.invoiceEntries.map((entry) => (
                                      <div key={entry.id} style={{ fontSize: '0.9rem', color: '#374151' }}>
                                        {entry.invoice_number || '-'} • {entry.issuer || '-'} • <strong>{formatAmountDe(entry.amount)} EUR</strong>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {row.materialEntries.length > 0 && (
                                <div>
                                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Material</div>
                                  <div style={{ display: 'grid', gap: '0.25rem' }}>
                                    {row.materialEntries.map((entry) => (
                                      <div key={entry.id} style={{ fontSize: '0.9rem', color: '#374151' }}>
                                        {entry.bezeichnung || '-'} • {formatAmountDe(entry.menge)} {entry.einheit || ''} • <strong>{formatAmountDe(entry.amount)} EUR</strong>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {showDetails && (
                  <div style={{ marginTop: '1rem' }}>
                    {statsData.entries.length === 0 ? (
                      <div style={{ color: '#6b7280' }}>Keine Einsaetze vorhanden.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: '0.5rem' }}>
                        {statsData.entries.map(entry => (
                          <div
                            key={entry.uid}
                            style={{
                              padding: '0.5rem',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.5rem',
                              background: 'white'
                            }}
                          >
                            <div style={{ fontWeight: '600' }}>
                              {entry.datum} • {entry.mitarbeiterName} • {entry.stunden}h
                            </div>
                            {entry.beschreibung && (
                              <div style={{ color: '#6b7280', marginTop: '0.25rem' }}>
                                {entry.beschreibung}
                              </div>
                            )}
                            {entry.source === 'manual' && (
                              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                                <button className="btn" onClick={() => openEditManualHours(entry)}>
                                  <Edit3 size={14} /> Bearbeiten
                                </button>
                                <button
                                  className="btn"
                                  style={{ color: '#dc2626' }}
                                  onClick={() => deleteManualHours(entry)}
                                >
                                  <Trash2 size={14} /> Löschen
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {showInvoiceDetails && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <h4 style={{ margin: 0, fontSize: '1.05rem' }}>Rechnungszuordnungen</h4>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button className="btn btn-primary" onClick={openCreateManualInvoice}>
                          <Plus size={14} /> Rechnung manuell hinzufügen
                        </button>
                        <button className="btn" onClick={refreshInvoiceAssignments} disabled={invoiceAssignmentsLoading}>
                          {invoiceAssignmentsLoading ? 'Lädt...' : 'Rechnungen aktualisieren'}
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <div className="card" style={{ margin: 0, background: '#f9fafb' }}>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Rechnungen</div>
                        <div style={{ fontWeight: 700 }}>{invoiceTotals.invoiceCount}</div>
                      </div>
                      <div className="card" style={{ margin: 0, background: '#f9fafb' }}>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Positionen</div>
                        <div style={{ fontWeight: 700 }}>{invoiceTotals.positionsCount}</div>
                      </div>
                      <div className="card" style={{ margin: 0, background: '#f9fafb' }}>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Summe Rechnungen</div>
                        <div style={{ fontWeight: 700 }}>{formatAmountDe(invoiceTotals.invoiceAmount)} EUR</div>
                      </div>
                      <div className="card" style={{ margin: 0, background: '#f9fafb' }}>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Summe Positionen</div>
                        <div style={{ fontWeight: 700 }}>{formatAmountDe(invoiceTotals.positionsAmount)} EUR</div>
                      </div>
                    </div>

                    {invoiceAssignments.length === 0 ? (
                      <div style={{ color: '#6b7280' }}>Keine zugeordneten Rechnungen vorhanden.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {invoiceAssignments.map((invoice) => {
                          const posSum = (invoice.positionen || []).reduce((sum, pos) => sum + (Number(pos.line_total) || 0), 0);
                          return (
                            <div key={invoice.id} className="card" style={{ margin: 0, background: '#fff' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                                <div>
                                  <div style={{ fontWeight: 700 }}>
                                    {invoice.invoice_number || '-'} • {invoice.issuer || '-'}
                                  </div>
                                  <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                                    Datum: {invoice.date || '-'} • Datei: {invoice.datei_name || '-'}
                                  </div>
                                </div>
                                <div style={{ textAlign: 'right', fontSize: '0.85rem' }}>
                                  <div>Rechnungsbetrag: <strong>{formatAmountDe(invoice.amount || 0)} EUR</strong></div>
                                  <div>Positionen: <strong>{(invoice.positionen || []).length}</strong> • Summe: <strong>{formatAmountDe(posSum)} EUR</strong></div>
                                  <div style={{ marginTop: '0.5rem' }}>
                                    <button
                                      className="btn"
                                      style={{ color: '#dc2626' }}
                                      onClick={() => openInvoiceDeleteDialog(invoice)}
                                    >
                                      <Trash2 size={14} /> Rechnung löschen
                                    </button>
                                  </div>
                                </div>
                              </div>
                              {(invoice.positionen || []).length === 0 ? (
                                <div style={{ color: '#6b7280' }}>Keine Positionen vorhanden.</div>
                              ) : (
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                    <thead>
                                      <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                                        <th style={{ padding: '0.4rem' }}>Art.-Nr.</th>
                                        <th style={{ padding: '0.4rem' }}>Beschreibung</th>
                                        <th style={{ padding: '0.4rem', textAlign: 'right' }}>Menge</th>
                                        <th style={{ padding: '0.4rem' }}>Einheit</th>
                                        <th style={{ padding: '0.4rem', textAlign: 'right' }}>Einzelpreis</th>
                                        <th style={{ padding: '0.4rem', textAlign: 'right' }}>Betrag</th>
                                        <th style={{ padding: '0.4rem' }}></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(invoice.positionen || []).map((pos) => (
                                        <tr key={pos.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                                          <td style={{ padding: '0.4rem' }}>{pos.article_no || '-'}</td>
                                          <td style={{ padding: '0.4rem' }}>{pos.description || '-'}</td>
                                          <td style={{ padding: '0.4rem', textAlign: 'right' }}>{pos.quantity ?? '-'}</td>
                                          <td style={{ padding: '0.4rem' }}>{pos.unit || '-'}</td>
                                          <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatAmountDe(pos.unit_price)}</td>
                                          <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatAmountDe(pos.line_total)}</td>
                                          <td style={{ padding: '0.4rem' }}>
                                            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                              <button className="btn" onClick={() => openOriginalInvoice(invoice.id)} disabled={invoiceFileLoadingId === invoice.id}>
                                                {invoiceFileLoadingId === invoice.id ? 'Lädt...' : 'Original anzeigen'}
                                              </button>
                                              <button className="btn" onClick={() => startEditPosition(pos, invoice)}>
                                                <Edit3 size={14} /> Bearbeiten
                                              </button>
                                              <button className="btn" style={{ color: '#dc2626' }} onClick={() => deletePosition(pos.id)}>
                                                <Trash2 size={14} /> Löschen
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {showMaterialDetails && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <h4 style={{ margin: 0, fontSize: '1.05rem' }}>Verbrauchte Materialien</h4>
                      <button className="btn" onClick={refreshMaterialAssignments} disabled={materialAssignmentsLoading}>
                        {materialAssignmentsLoading ? 'Lädt...' : 'Material aktualisieren'}
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <div className="card" style={{ margin: 0, background: '#f9fafb' }}>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Positionen</div>
                        <div style={{ fontWeight: 700 }}>{materialTotals.count}</div>
                      </div>
                      <div className="card" style={{ margin: 0, background: '#f9fafb' }}>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Gesamtkosten</div>
                        <div style={{ fontWeight: 700 }}>{formatAmountDe(materialTotals.amount)} EUR</div>
                      </div>
                      <div className="card" style={{ margin: 0, background: '#f9fafb' }}>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Kategorien</div>
                        <div style={{ fontWeight: 700 }}>{Object.keys(materialTotals.byCategory).length}</div>
                      </div>
                    </div>

                    {materialAssignments.length === 0 ? (
                      <div style={{ color: '#6b7280' }}>Keine Materialbuchungen vorhanden.</div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', background: '#fff', borderRadius: '0.75rem' }}>
                          <thead>
                            <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                              <th style={{ padding: '0.5rem' }}>Datum</th>
                              <th style={{ padding: '0.5rem' }}>Kategorie</th>
                              <th style={{ padding: '0.5rem' }}>Bezeichnung</th>
                              <th style={{ padding: '0.5rem', textAlign: 'right' }}>Menge</th>
                              <th style={{ padding: '0.5rem' }}>Einheit</th>
                              <th style={{ padding: '0.5rem', textAlign: 'right' }}>Preis/EH</th>
                              <th style={{ padding: '0.5rem', textAlign: 'right' }}>Gesamt</th>
                            </tr>
                          </thead>
                          <tbody>
                            {materialAssignments.map((entry) => (
                              <tr key={entry.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '0.5rem' }}>{entry.datum}</td>
                                <td style={{ padding: '0.5rem' }}>{entry.kategorie || '-'}</td>
                                <td style={{ padding: '0.5rem' }}>
                                  <div style={{ fontWeight: 600 }}>{entry.bezeichnung || '-'}</div>
                                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                    {entry.artikel_name ? `${entry.artikel_name} (${entry.artikel_nummer || '-'})` : 'Freie Eingabe'}
                                  </div>
                                  {entry.bemerkung && (
                                    <div style={{ fontSize: '0.8rem', color: '#4b5563', marginTop: '0.2rem' }}>{entry.bemerkung}</div>
                                  )}
                                </td>
                                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatAmountDe(entry.menge)}</td>
                                <td style={{ padding: '0.5rem' }}>{entry.einheit || '-'}</td>
                                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatAmountDe(entry.preis_pro_einheit)} EUR</td>
                                <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{formatAmountDe(entry.gesamtpreis)} EUR</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {loading && baustellen.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            Lade Baustellen...
          </div>
        ) : filteredBaustellen.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            Keine Baustellen gefunden
          </div>
        ) : (
          !statsBaustelle ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                  <th style={{ padding: '0.75rem 0.5rem' }}>
                    <button type="button" className="btn" onClick={() => toggleSort('farbe')}>{renderSortLabel('Farbe', 'farbe')}</button>
                  </th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>
                    <button type="button" className="btn" onClick={() => toggleSort('kunde_name')}>{renderSortLabel('Kundenname', 'kunde_name')}</button>
                  </th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>
                    <button type="button" className="btn" onClick={() => toggleSort('nummer')}>{renderSortLabel('Baustellennummer', 'nummer')}</button>
                  </th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>
                    <button type="button" className="btn" onClick={() => toggleSort('start_datum')}>{renderSortLabel('Startdatum', 'start_datum')}</button>
                  </th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>
                    <button type="button" className="btn" onClick={() => toggleSort('status')}>{renderSortLabel('Status', 'status')}</button>
                  </th>
                  <th style={{ padding: '0.75rem 0.5rem', width: '110px' }}>Tools</th>
                </tr>
              </thead>
              <tbody>
                {filteredBaustellen.map((b, index) => {
                  const isHovered = hoveredBaustelleId === b.id;
                  return (
                  <tr
                    key={b.id}
                    style={{
                      borderTop: '1px solid #e5e7eb',
                      background: isHovered ? '#dbeafe' : (index % 2 === 0 ? '#ffffff' : '#f9fafb'),
                      cursor: 'pointer',
                      transition: 'background 120ms ease'
                    }}
                    onMouseEnter={() => setHoveredBaustelleId(b.id)}
                    onMouseLeave={() => setHoveredBaustelleId(null)}
                    onClick={() => loadBaustelleStats(b)}
                  >
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <div style={{ width: '18px', height: '18px', background: b.farbe || '#3B82F6', borderRadius: '4px' }} />
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', fontWeight: 600 }}>{b.kunde_name || '-'}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{b.nummer || '-'}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{b.start_datum || '-'}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <StatusBadge status={b.status} />
                        {b.nextcloud_ordner_pfad ? (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '999px',
                              background: '#dbeafe',
                              color: '#1d4ed8',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}
                            title={`Nextcloud verbunden: /${b.nextcloud_ordner_pfad}`}
                          >
                            Nextcloud
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                        <button
                          type="button"
                          className="btn"
                          title="Baustelle bearbeiten"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditBaustelle(b, { openFromDetails: true });
                          }}
                          style={{ padding: '0.35rem 0.5rem' }}
                        >
                          <Settings size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn"
                          title="Masterliste öffnen"
                          onClick={(e) => {
                            e.stopPropagation();
                            openMasterListShortcut(b);
                          }}
                          style={{ padding: '0.35rem 0.5rem' }}
                        >
                          <TableProperties size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn"
                          title="Kosten einlesen"
                          onClick={(e) => {
                            e.stopPropagation();
                            openImportWizard(b);
                          }}
                          style={{ padding: '0.35rem 0.5rem' }}
                        >
                          <FileSpreadsheet size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          ) : null
        )}
      </div>

      {showImportWizard && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 2450,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
          onClick={closeImportWizard}
        >
          <div
            className="card"
            style={{ width: 'min(1100px, 100%)', maxHeight: '90vh', overflow: 'auto', margin: 0, background: '#fff' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0 }}>Kosten einlesen</h3>
                <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                  {importWizardBaustelle?.nummer || '-'} {importWizardBaustelle?.name || ''}
                </div>
              </div>
              <button className="btn" onClick={closeImportWizard}><X size={16} /> Schließen</button>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {[1, 2, 3, 4].map((step) => (
                <span
                  key={step}
                  style={{
                    padding: '0.35rem 0.65rem',
                    borderRadius: '999px',
                    background: importWizardStep === step ? '#dbeafe' : '#f3f4f6',
                    color: importWizardStep === step ? '#1d4ed8' : '#4b5563',
                    fontWeight: 600,
                    fontSize: '0.8rem'
                  }}
                >
                  Schritt {step}
                </span>
              ))}
            </div>

            {importWizardError && (
              <div className="card" style={{ margin: '0 0 1rem 0', background: '#fef2f2', color: '#991b1b' }}>
                {importWizardError}
              </div>
            )}

            {importWizardStep === 1 && (
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>1. Nextcloud-Verzeichnis auswählen</div>
                  <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                    Für diese Baustelle ist noch kein Nextcloud-Ordner hinterlegt.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button className="btn" onClick={() => loadImportDirectories(importWizardPath)} disabled={importWizardLoading}>
                    {importWizardLoading ? 'Lädt...' : 'Aktualisieren'}
                  </button>
                  <button className="btn" onClick={() => {
                    const parts = importWizardPath.split('/').filter(Boolean);
                    parts.pop();
                    loadImportDirectories(parts.join('/'));
                  }} disabled={importWizardLoading || !importWizardPath}>
                    Nach oben
                  </button>
                  <span style={{ color: '#374151' }}>Aktueller Ordner: /{importWizardPath || ''}</span>
                </div>
                <div style={{ display: 'grid', gap: '0.5rem', maxHeight: '320px', overflowY: 'auto' }}>
                  {importWizardDirs.length === 0 ? (
                    <div style={{ color: '#6b7280' }}>Keine Unterordner gefunden.</div>
                  ) : importWizardDirs.map((entry) => (
                    <button
                      key={entry.path}
                      className="btn"
                      onClick={() => loadImportDirectories(entry.path)}
                      style={{ justifyContent: 'space-between' }}
                    >
                      <span>{entry.name}</span>
                      <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>/ {entry.path}</span>
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn btn-primary" onClick={saveImportWizardPath} disabled={!importWizardPath || importWizardLoading}>
                    <Check size={16} /> Ordner verwenden
                  </button>
                </div>
              </div>
            )}

            {importWizardStep === 2 && (
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ fontWeight: 600 }}>2. Unterordner `Eingangsrechnungen` prüfen</div>
                <div style={{ color: '#6b7280' }}>
                  Baustellenordner: /{importWizardBaustelle?.nextcloud_ordner_pfad || ''}
                </div>
                <div className="card" style={{ margin: 0, background: importWizardFolderExists ? '#ecfdf5' : '#fef2f2' }}>
                  {importWizardFolderExists
                    ? 'Der Ordner Eingangsrechnungen ist vorhanden.'
                    : 'Der Ordner Eingangsrechnungen wurde noch nicht gefunden.'}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {!importWizardFolderExists && (
                    <button className="btn btn-primary" onClick={ensureImportFolder} disabled={importWizardLoading}>
                      <Plus size={16} /> Ordner erstellen
                    </button>
                  )}
                  {importWizardFolderExists && (
                    <button className="btn btn-primary" onClick={() => refreshImportFiles(importWizardBaustelle)} disabled={importWizardLoading}>
                      Weiter
                    </button>
                  )}
                </div>
              </div>
            )}

            {importWizardStep === 3 && (
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ fontWeight: 600 }}>3. Tabellen-Datei aus `Eingangsrechnungen` auswählen</div>
                <div style={{ color: '#6b7280' }}>Es werden `.ods` und `.xlsx` angeboten.</div>
                <select
                  className="input"
                  value={selectedImportFile}
                  onChange={(e) => setSelectedImportFile(e.target.value)}
                >
                  <option value="">Datei wählen...</option>
                  {importWizardFiles.map((file) => (
                    <option key={file.path} value={file.name}>{file.name}</option>
                  ))}
                </select>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button className="btn" onClick={() => refreshImportFiles(importWizardBaustelle)} disabled={importWizardLoading}>
                    Aktualisieren
                  </button>
                  <button className="btn btn-primary" onClick={loadImportPreview} disabled={!selectedImportFile || importWizardLoading}>
                    Datei einlesen
                  </button>
                </div>
              </div>
            )}

            {importWizardStep === 4 && (
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>4. Datensätze auswählen und importieren</div>
                    <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                      Es wurden {importPreviewRows.length} Zeilen aus der Tabelle erkannt.
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button className="btn" onClick={() => setSelectedImportRows(importPreviewRows.map((row) => row.id))}>Alle wählen</button>
                    <button className="btn" onClick={() => setSelectedImportRows([])}>Keine wählen</button>
                    <button className="btn btn-primary" onClick={importSelectedRows} disabled={selectedImportRows.length === 0 || importWizardLoading}>
                      Import
                    </button>
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                        <th style={{ padding: '0.5rem' }}></th>
                        <th style={{ padding: '0.5rem' }}>A</th>
                        <th style={{ padding: '0.5rem' }}>B</th>
                        <th style={{ padding: '0.5rem' }}>C</th>
                        <th style={{ padding: '0.5rem' }}>D</th>
                        <th style={{ padding: '0.5rem' }}>E</th>
                        <th style={{ padding: '0.5rem' }}>F</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreviewRows.map((row) => (
                        <tr key={row.id} style={{ borderTop: '1px solid #f3f4f6', background: selectedImportRows.includes(row.id) ? '#eff6ff' : '#fff' }}>
                          <td style={{ padding: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={selectedImportRows.includes(row.id)}
                              onChange={(e) => {
                                setSelectedImportRows((prev) => e.target.checked ? [...prev, row.id] : prev.filter((id) => id !== row.id));
                              }}
                            />
                          </td>
                          <td style={{ padding: '0.5rem' }}>{row.col_a}</td>
                          <td style={{ padding: '0.5rem' }}>{formatImportPreviewDate(row.col_b)}</td>
                          <td style={{ padding: '0.5rem' }}>{row.col_c}</td>
                          <td style={{ padding: '0.5rem' }}>{formatImportPreviewAmount(row.col_d)}</td>
                          <td style={{ padding: '0.5rem' }}>{formatImportPreviewAmount(row.col_e)}</td>
                          <td style={{ padding: '0.5rem' }}>{formatImportPreviewAmount(row.col_f)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showManualHoursModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 2450,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div className="card" style={{ width: '100%', maxWidth: '560px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.1rem' }}>
              {manualHoursForm.id ? 'Manuelle Stunden bearbeiten' : 'Stunden manuell hinzufügen'}
            </h3>

            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>
                  Datum
                </label>
                <input
                  type="date"
                  className="input"
                  value={manualHoursForm.datum}
                  onChange={(e) => setManualHoursForm(prev => ({ ...prev, datum: e.target.value }))}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>
                  Anzahl der Stunden
                </label>
                <input
                  type="number"
                  className="input"
                  min="0"
                  step="0.25"
                  value={manualHoursForm.stunden}
                  onChange={(e) => setManualHoursForm(prev => ({ ...prev, stunden: e.target.value }))}
                  placeholder="z.B. 8"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>
                  Beschreibung
                </label>
                <textarea
                  className="input"
                  rows={4}
                  value={manualHoursForm.beschreibung}
                  onChange={(e) => setManualHoursForm(prev => ({ ...prev, beschreibung: e.target.value }))}
                  placeholder="Beschreibung"
                />
              </div>
            </div>

            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn" onClick={closeManualHoursModal} disabled={manualHoursSaving}>
                Abbrechen
              </button>
              <button className="btn btn-primary" onClick={saveManualHours} disabled={manualHoursSaving}>
                {manualHoursSaving ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showManualInvoiceModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 2455,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div className="card" style={{ width: '100%', maxWidth: '760px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.1rem' }}>Rechnung manuell hinzufügen</h3>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <input
                  className="input"
                  placeholder="Dateiname"
                  value={manualInvoiceForm.datei_name}
                  onChange={(e) => setManualInvoiceForm((prev) => ({ ...prev, datei_name: e.target.value }))}
                />
                <input
                  className="input"
                  placeholder="Lieferant"
                  value={manualInvoiceForm.issuer}
                  onChange={(e) => setManualInvoiceForm((prev) => ({ ...prev, issuer: e.target.value }))}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <input
                  className="input"
                  placeholder="Rechnungsnummer"
                  value={manualInvoiceForm.invoice_number}
                  onChange={(e) => setManualInvoiceForm((prev) => ({ ...prev, invoice_number: e.target.value }))}
                />
                <input
                  type="date"
                  className="input"
                  value={manualInvoiceForm.date}
                  onChange={(e) => setManualInvoiceForm((prev) => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                <input
                  className="input"
                  placeholder="Netto"
                  value={manualInvoiceForm.net_amount}
                  onChange={(e) => setManualInvoiceForm((prev) => ({ ...prev, net_amount: e.target.value }))}
                />
                <input
                  className="input"
                  placeholder="MwSt."
                  value={manualInvoiceForm.tax}
                  onChange={(e) => setManualInvoiceForm((prev) => ({ ...prev, tax: e.target.value }))}
                />
                <input
                  className="input"
                  placeholder="Brutto"
                  value={manualInvoiceForm.amount}
                  onChange={(e) => setManualInvoiceForm((prev) => ({ ...prev, amount: e.target.value }))}
                />
              </div>
              <textarea
                className="input"
                rows={4}
                placeholder="Bemerkung"
                value={manualInvoiceForm.bemerkung}
                onChange={(e) => setManualInvoiceForm((prev) => ({ ...prev, bemerkung: e.target.value }))}
              />
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn" onClick={closeManualInvoiceModal} disabled={manualInvoiceSaving}>
                Abbrechen
              </button>
              <button className="btn btn-primary" onClick={saveManualInvoice} disabled={manualInvoiceSaving}>
                {manualInvoiceSaving ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNextcloudPicker && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 2500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div className="card" style={{ width: '100%', maxWidth: '760px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Nextcloud-Verzeichnis wählen</h3>
              <button onClick={closeNextcloudPicker} className="btn">
                <X size={16} /> Schließen
              </button>
            </div>

            <div style={{ marginBottom: '0.75rem', fontSize: '0.9rem' }}>
              <strong>Aktueller Pfad:</strong> {nextcloudCurrentPath ? `/${nextcloudCurrentPath}` : '/'}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button className="btn" onClick={() => loadNextcloudDirectories('')} disabled={nextcloudLoading}>
                Root
              </button>
              <button className="btn" onClick={goToNextcloudParent} disabled={nextcloudLoading || !nextcloudCurrentPath}>
                Eine Ebene hoch
              </button>
            </div>

            {nextcloudError && (
              <div style={{ color: '#b91c1c', background: '#fee2e2', borderRadius: '0.5rem', padding: '0.5rem', marginBottom: '0.75rem' }}>
                {nextcloudError}
              </div>
            )}

            <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', maxHeight: '45vh', overflowY: 'auto', padding: '0.5rem' }}>
              {nextcloudLoading ? (
                <div style={{ color: '#6b7280' }}>Lade Verzeichnisse...</div>
              ) : nextcloudDirs.length === 0 ? (
                <div style={{ color: '#6b7280' }}>Keine Unterverzeichnisse vorhanden.</div>
              ) : (
                nextcloudDirs.map((dir) => (
                  <button
                    key={dir.path || dir.name}
                    className="btn"
                    onClick={() => loadNextcloudDirectories(dir.path)}
                    style={{ width: '100%', marginBottom: '0.35rem', justifyContent: 'flex-start' }}
                    title={`/${dir.path}`}
                  >
                    {dir.name}
                  </button>
                ))
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-primary" onClick={selectNextcloudPath}>
                Diesen Ordner verwenden
              </button>
            </div>
          </div>
        </div>
      )}

      {positionEditing && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 2460,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div className="card" style={{ width: '100%', maxWidth: '640px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1.1rem' }}>Rechnungsposition bearbeiten</h3>
            <div style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{positionEditing.invoiceLabel}</div>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Art.-Nr.</label>
                  <input className="input" value={positionEditing.article_no} onChange={(e) => setPositionEditing((prev) => ({ ...prev, article_no: e.target.value }))} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Einheit</label>
                  <input className="input" value={positionEditing.unit} onChange={(e) => setPositionEditing((prev) => ({ ...prev, unit: e.target.value }))} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Beschreibung</label>
                <textarea className="input" rows={3} value={positionEditing.description} onChange={(e) => setPositionEditing((prev) => ({ ...prev, description: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Menge</label>
                  <input className="input" value={positionEditing.quantity} onChange={(e) => setPositionEditing((prev) => ({ ...prev, quantity: e.target.value }))} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Einzelpreis</label>
                  <input className="input" value={positionEditing.unit_price} onChange={(e) => setPositionEditing((prev) => ({ ...prev, unit_price: e.target.value }))} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Betrag</label>
                  <input className="input" value={positionEditing.line_total} onChange={(e) => setPositionEditing((prev) => ({ ...prev, line_total: e.target.value }))} />
                </div>
              </div>
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn" onClick={closePositionEdit} disabled={positionSaving}>Abbrechen</button>
              <button className="btn btn-primary" onClick={savePositionEdit} disabled={positionSaving}>
                {positionSaving ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {invoiceDeleteDialog.open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 2470,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div className="card" style={{ width: '100%', maxWidth: '640px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1.1rem' }}>Rechnung löschen</h3>
            <div style={{ marginBottom: '0.75rem' }}>
              <strong>{invoiceDeleteDialog.invoice?.invoice_number || '-'}</strong>
              {' '}• {invoiceDeleteDialog.invoice?.issuer || '-'}
            </div>
            <div style={{ color: '#6b7280', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
              Diese Aktion löscht die Rechnung inklusive aller Positionen aus der Baustellen-Datenbank.
            </div>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <input
                  type="radio"
                  checked={invoiceDeleteDialog.fileAction === 'move_to_inbox'}
                  onChange={() => setInvoiceDeleteDialog((prev) => ({ ...prev, fileAction: 'move_to_inbox' }))}
                />
                <span>Datei in den allgemeinen Rechnungseingangsordner verschieben</span>
              </label>
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <input
                  type="radio"
                  checked={invoiceDeleteDialog.fileAction === 'delete'}
                  onChange={() => setInvoiceDeleteDialog((prev) => ({ ...prev, fileAction: 'delete' }))}
                />
                <span>Datei aus dem Nextcloud-Verzeichnis der Baustelle löschen</span>
              </label>
            </div>
            <div style={{
              marginTop: '0.9rem',
              background: '#fee2e2',
              color: '#991b1b',
              borderRadius: '0.5rem',
              padding: '0.65rem',
              fontSize: '0.88rem'
            }}>
              Hinweis: Beim Löschen der Datei kann die Rechnung vollständig aus dem System verschwinden.
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn" onClick={closeInvoiceDeleteDialog} disabled={invoiceDeleteDialog.submitting}>
                Abbrechen
              </button>
              <button
                className="btn"
                style={{ background: '#dc2626', color: '#fff', borderColor: '#dc2626' }}
                onClick={confirmDeleteInvoice}
                disabled={invoiceDeleteDialog.submitting}
              >
                {invoiceDeleteDialog.submitting ? 'Löscht...' : 'Rechnung löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BaustelleForm({
  baustelle,
  isEditing,
  stundenverrechnungOverrides,
  editingStundenverrechnungOverride,
  newStundenverrechnungOverride,
  rechnungsaufschlagOverrides,
  editingRechnungsaufschlagOverride,
  newRechnungsaufschlagOverride,
  onChange,
  onSave,
  onCancel,
  onConnectNextcloud,
  onStartNewStundenverrechnungOverride,
  onEditStundenverrechnungOverride,
  onSaveStundenverrechnungOverride,
  onCancelStundenverrechnungOverride,
  onChangeNewStundenverrechnungOverride,
  onChangeEditingStundenverrechnungOverride,
  onDeleteStundenverrechnungOverride,
  onStartNewRechnungsaufschlagOverride,
  onEditRechnungsaufschlagOverride,
  onSaveRechnungsaufschlagOverride,
  onCancelRechnungsaufschlagOverride,
  onChangeNewRechnungsaufschlagOverride,
  onChangeEditingRechnungsaufschlagOverride,
  onDeleteRechnungsaufschlagOverride,
}) {
  return (
    <div className="card" style={{ marginBottom: '1.5rem', background: '#f9fafb' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
          {isEditing ? 'Baustelle bearbeiten' : 'Neue Baustelle'}
        </h3>
      </div>

      <div style={{ display: 'grid', gap: '1rem' }}>
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Nummer
            </label>
            <input
              type="text"
              value={baustelle.nummer || ''}
              className="input"
              disabled
              placeholder="Wird automatisch vergeben"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Farbe
            </label>
            <input
              type="color"
              value={baustelle.farbe || '#3B82F6'}
              className="input"
              onChange={(e) => onChange({ ...baustelle, farbe: e.target.value })}
              style={{ height: '2.5rem', padding: '0.25rem' }}
            />
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Name *
          </label>
          <input
            type="text"
            value={baustelle.name}
            onChange={(e) => onChange({ ...baustelle, name: e.target.value })}
            className="input"
          />
        </div>

        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Kunde *
            </label>
            <input
              type="text"
              value={baustelle.kunde_name}
              onChange={(e) => onChange({ ...baustelle, kunde_name: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Status
            </label>
            <select
              className="input"
              value={baustelle.status}
              onChange={(e) => onChange({ ...baustelle, status: e.target.value })}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Start-Datum
            </label>
            <input
              type="date"
              value={baustelle.start_datum || ''}
              onChange={(e) => onChange({ ...baustelle, start_datum: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Ende-Datum
            </label>
            <input
              type="date"
              value={baustelle.ende_datum || ''}
              onChange={(e) => onChange({ ...baustelle, ende_datum: e.target.value })}
              className="input"
            />
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            Beschreibung
          </label>
          <textarea
            rows={3}
            value={baustelle.beschreibung || ''}
            onChange={(e) => onChange({ ...baustelle, beschreibung: e.target.value })}
            className="input"
          />
        </div>

        {isEditing && (
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Nextcloud-Verzeichnis
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={onConnectNextcloud} type="button" className="btn">
                mit Nextcloud verbinden
              </button>
              <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                {baustelle.nextcloud_ordner_pfad ? `/${baustelle.nextcloud_ordner_pfad}` : 'Kein Ordner gewählt'}
              </span>
            </div>
          </div>
        )}

        {isEditing && (
          <div className="card" style={{ margin: 0, background: '#ffffff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div style={{ fontWeight: 600 }}>Stundenverrechnungspauschale Baustelle</div>
              {!newStundenverrechnungOverride && !editingStundenverrechnungOverride && (
                <button type="button" onClick={onStartNewStundenverrechnungOverride} className="btn btn-primary">
                  <Plus size={16} /> Override hinzufügen
                </button>
              )}
            </div>

            {newStundenverrechnungOverride && (
              <StundenverrechnungOverrideForm
                eintrag={newStundenverrechnungOverride}
                onChange={onChangeNewStundenverrechnungOverride}
                onSave={() => onSaveStundenverrechnungOverride(newStundenverrechnungOverride)}
                onCancel={onCancelStundenverrechnungOverride}
              />
            )}

            {editingStundenverrechnungOverride && (
              <StundenverrechnungOverrideForm
                eintrag={editingStundenverrechnungOverride}
                onChange={onChangeEditingStundenverrechnungOverride}
                onSave={() => onSaveStundenverrechnungOverride(editingStundenverrechnungOverride)}
                onCancel={onCancelStundenverrechnungOverride}
              />
            )}

            {!newStundenverrechnungOverride && !editingStundenverrechnungOverride && (
              (stundenverrechnungOverrides || []).length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Kein Override hinterlegt. Dann gilt die globale Historie.</div>
              ) : (
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {(stundenverrechnungOverrides || []).map((eintrag) => (
                    <div key={eintrag.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem' }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{eintrag.gueltig_ab}</div>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                          {Number(eintrag.wert || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR / Stunde
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button type="button" onClick={() => onEditStundenverrechnungOverride(eintrag)} className="btn">Bearbeiten</button>
                        <button type="button" onClick={() => onDeleteStundenverrechnungOverride(eintrag.id)} className="btn" style={{ color: '#dc2626' }}>Löschen</button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}

        {isEditing && (
          <div className="card" style={{ margin: 0, background: '#ffffff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div style={{ fontWeight: 600 }}>Rechnungs-Aufschlag Baustelle</div>
              {!newRechnungsaufschlagOverride && !editingRechnungsaufschlagOverride && (
                <button type="button" onClick={onStartNewRechnungsaufschlagOverride} className="btn btn-primary">
                  <Plus size={16} /> Override hinzufügen
                </button>
              )}
            </div>

            {newRechnungsaufschlagOverride && (
              <RechnungsaufschlagOverrideForm
                eintrag={newRechnungsaufschlagOverride}
                onChange={onChangeNewRechnungsaufschlagOverride}
                onSave={() => onSaveRechnungsaufschlagOverride(newRechnungsaufschlagOverride)}
                onCancel={onCancelRechnungsaufschlagOverride}
              />
            )}

            {editingRechnungsaufschlagOverride && (
              <RechnungsaufschlagOverrideForm
                eintrag={editingRechnungsaufschlagOverride}
                onChange={onChangeEditingRechnungsaufschlagOverride}
                onSave={() => onSaveRechnungsaufschlagOverride(editingRechnungsaufschlagOverride)}
                onCancel={onCancelRechnungsaufschlagOverride}
              />
            )}

            {!newRechnungsaufschlagOverride && !editingRechnungsaufschlagOverride && (
              (rechnungsaufschlagOverrides || []).length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Kein Override hinterlegt. Dann gilt die globale Historie.</div>
              ) : (
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {(rechnungsaufschlagOverrides || []).map((eintrag) => (
                    <div key={eintrag.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem' }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{eintrag.gueltig_ab}</div>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                          {Number(eintrag.wert || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button type="button" onClick={() => onEditRechnungsaufschlagOverride(eintrag)} className="btn">Bearbeiten</button>
                        <button type="button" onClick={() => onDeleteRechnungsaufschlagOverride(eintrag.id)} className="btn" style={{ color: '#dc2626' }}>Löschen</button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="btn">
            <X size={18} /> Abbrechen
          </button>
          <button
            onClick={onSave}
            className="btn btn-primary"
            disabled={!baustelle.name || !baustelle.kunde_name}
          >
            <Check size={18} /> Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function StundenverrechnungOverrideForm({ eintrag, onChange, onSave, onCancel }) {
  return (
    <div className="card" style={{ margin: 0, background: '#f9fafb' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <input
          type="date"
          value={eintrag.gueltig_ab || ''}
          onChange={(e) => onChange({ ...eintrag, gueltig_ab: e.target.value })}
          className="input"
        />
        <input
          type="number"
          step="0.01"
          value={eintrag.wert || ''}
          onChange={(e) => onChange({ ...eintrag, wert: e.target.value })}
          className="input"
          placeholder="Wert pro Stunde"
        />
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} className="btn"><X size={16} /> Abbrechen</button>
        <button type="button" onClick={onSave} className="btn btn-primary"><Check size={16} /> Speichern</button>
      </div>
    </div>
  );
}

function RechnungsaufschlagOverrideForm({ eintrag, onChange, onSave, onCancel }) {
  return (
    <div className="card" style={{ margin: 0, background: '#f9fafb' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <input
          type="date"
          value={eintrag.gueltig_ab || ''}
          onChange={(e) => onChange({ ...eintrag, gueltig_ab: e.target.value })}
          className="input"
        />
        <input
          type="number"
          step="0.01"
          value={eintrag.wert || ''}
          onChange={(e) => onChange({ ...eintrag, wert: e.target.value })}
          className="input"
          placeholder="Aufschlag in %"
        />
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} className="btn"><X size={16} /> Abbrechen</button>
        <button type="button" onClick={onSave} className="btn btn-primary"><Check size={16} /> Speichern</button>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    GEPLANT: { label: 'Geplant', bg: '#fef3c7', color: '#92400e' },
    AKTIV: { label: 'Aktiv', bg: '#dcfce7', color: '#166534' },
    PAUSIERT: { label: 'Pausiert', bg: '#e0f2fe', color: '#075985' },
    ABGESCHLOSSEN: { label: 'Abgeschlossen', bg: '#e5e7eb', color: '#374151' }
  };

  const badge = map[status] || { label: status || '-', bg: '#e5e7eb', color: '#374151' };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.2rem 0.5rem',
        borderRadius: '999px',
        background: badge.bg,
        color: badge.color,
        fontSize: '0.75rem',
        fontWeight: 600
      }}
    >
      {badge.label}
    </span>
  );
}

export default BaustellenView;
